import { action, makeObservable, observable, computed, runInAction, reaction } from 'mobx';
import { eventStream, TTickEvent } from 'src/replay';
import { PlaybackScheduler, PlaybackSpeed } from 'src/replay/PlaybackScheduler';
import { SegmentIterator } from 'src/replay/SegmentIterator';
import { TickData } from 'src/replay/SegmentSchema';
// import { BatchPlaybackProcessor } from 'src/replay/BatchPlaybackProcessor';
import MainStore from '.';
import { TQuote } from 'src/types';
import { TGranularity } from 'src/types/api.types';

export type TReplayMode = 'live' | 'paused' | 'replay';
export type TPlaybackSpeed = 1 | 2 | 5 | 10 | 20 | 50 | 100 | 1000;

export interface TReplayState {
    mode: TReplayMode;
    currentEpoch: number | null;
    playbackSpeed: TPlaybackSpeed;
    isPlaying: boolean;
    replayStartEpoch: number | null;
    replayEndEpoch: number | null;
    progress: number; // 0-100
}

/**
 * ReplayController: MobX store managing chart rewind and playback functionality.
 *
 * Modes:
 * - live: Normal real-time chart operation
 * - paused: Chart frozen at current time
 * - replay: Playing back historical data
 *
 * TESTING GUIDE:
 * ==============
 * For fast and reliable replay testing:
 *
 * 1. UI Testing (Quick Manual Verification):
 *    - Use speed selector: Choose 20x, 50x, 100x, or 1000x for fast testing
 *    - Enable "Test Mode" checkbox to skip most rendering (ultra-fast)
 *    - Use "Quick Skip" buttons to jump to 25%, 50%, 75%, or End
 *
 * 2. Programmatic Testing (Console):
 *    ```javascript
 *    // Get replay controller
 *    const replay = window.__mainStore.replayController;
 *
 *    // Option A: Quick validation test (automated)
 *    const result = await replay.runValidationTest();
 *    console.log('Test result:', result);
 *    // Returns: { success, duration, eventsProcessed, startEpoch, endEpoch, errors }
 *
 *    // Option B: Manual ultra-fast replay
 *    replay.enableTestMode(1000); // Render every 1000th event
 *    replay.setSpeed(1000);        // 1000x speed
 *    await replay.play();          // Start from current position
 *
 *    // Option C: Skip to specific points
 *    await replay.skipToProgress(50); // Jump to 50%
 *    await replay.play();             // Resume from there
 *    ```
 *
 * 3. Testing Different Time Ranges:
 *    - Select date in UI, set speed to 1000x, enable test mode
 *    - Or use: `await replay.skipToProgress(X)` to jump around
 *
 * 4. Performance Benchmarking:
 *    ```javascript
 *    const start = performance.now();
 *    replay.enableTestMode(5000); // Minimal rendering
 *    replay.setSpeed(1000);
 *    await replay.play();
 *    console.log(`Processed in ${performance.now() - start}ms`);
 *    ```
 *
 * Expected Performance:
 * - 1 day of data (~20K events): ~5-10 seconds at 1000x with test mode
 * - 1 week of data: ~30-60 seconds
 * - 1 month: ~2-4 minutes
 * - 1 year: ~20-30 minutes (with test mode enabled)
 */
export default class ReplayControllerStore {
    private mainStore: MainStore;
    private playbackTimer?: ReturnType<typeof setTimeout>;
    private animationFrameId?: number;
    private eventIterator?: Generator<TTickEvent, void, unknown>;
    private scheduler = new PlaybackScheduler({ speed: 1 });

    // Segment-based playback
    private segmentIterator?: SegmentIterator;
    private useSegmentStreaming = true; // NEW: Enable streaming playback from SegmentIterator
    // private batchProcessor?: BatchPlaybackProcessor; // no longer used; streaming replaces pre-batch processor

    // Chunked API fetching
    private chunkFetchAbortController?: AbortController;

    // Observable state
    mode: TReplayMode = 'live';
    currentEpoch: number | null = null;
    playbackSpeed: TPlaybackSpeed = 1;
    isPlaying = false;
    replayStartEpoch: number | null = null;
    replayEndEpoch: number | null = null;
    lastHistoricalDataEpoch: number | null = null; // Track the actual last time of loaded historical data
    lastFetchedPageEndEpoch: number | null = null; // Track end time of last fetched page for pagination
    isFetchingNextPage = false; // Prevent concurrent page fetches
    currentEventIndex = 0;
    totalEvents = 0;
    batchProgress = 0; // Progress for batch fast-forward (0-100)
    testMode = false; // When true, skip rendering for ultra-fast validation
    skipInterval = 100; // In test mode, render every Nth event
    
    // Auto-scroll tracking: when true, chart follows replay cursor; false when user manually scrolls away
    shouldFollowReplay = true;

    // Public observable: true while replay is loading data (shows overlay loader to hide rendering artifacts)
    isLoadingReplay = false;

    // Saved pre-replay chart settings so we can restore them when exiting replay
    private preReplayGranularity: TGranularity | undefined;
    private preReplayChartType: string | undefined;

    // Flag to skip the granularity/chartType change reaction during initial play() setup
    // This prevents the reaction from interfering with the play() initialization sequence
    private isInitializingReplay = false;

    // Progress tracking for streaming playback UI
    streamingProgress = {
        phase: 'idle' as 'idle' | 'loading_initial' | 'playing' | 'loading_segments' | 'loading_api_chunks',
        initialWindowLoaded: false,
        segmentsLoaded: 0,
        totalSegments: 0,
        currentEpoch: null as number | null,
        targetEpoch: null as number | null,
        // Chunk loading progress
        chunksLoaded: 0,
        totalChunks: 0,
    };

    constructor(mainStore: MainStore) {
        this.mainStore = mainStore;

        makeObservable(this, {
            mode: observable,
            currentEpoch: observable,
            playbackSpeed: observable,
            isPlaying: observable,
            replayStartEpoch: observable,
            replayEndEpoch: observable,
            lastHistoricalDataEpoch: observable,
            lastFetchedPageEndEpoch: observable,
            isFetchingNextPage: observable,
            currentEventIndex: observable,
            totalEvents: observable,
            batchProgress: observable,
            testMode: observable,
            skipInterval: observable,
            streamingProgress: observable,
            shouldFollowReplay: observable,
            isLoadingReplay: observable,

            progress: computed,
            canStepForward: computed,
            canStepBack: computed,
            availableTimeRange: computed,

            rewindTo: action.bound,
            play: action.bound,
            pause: action.bound,
            stop: action.bound,
            stepForward: action.bound,
            stepBack: action.bound,
            setSpeed: action.bound,
            jumpToLive: action.bound,
            setReplayRange: action.bound,
            fastForwardTo: action.bound,
            enableTestMode: action.bound,
            disableTestMode: action.bound,
            skipToProgress: action.bound,
        });

        // Handle granularity and chart type changes during replay
        reaction(
            () => ({ granularity: this.mainStore.state.granularity, chartType: this.mainStore.state.chartType }),
            async (
                { granularity, chartType }: { granularity: number | undefined; chartType?: string },
                previous?: { granularity: number | undefined; chartType?: string }
            ) => {
                // Skip reaction during initial play() setup - the play() method handles everything
                if (this.isInitializingReplay) {
                    console.log('[ReplayController] Skipping settings reaction - replay is initializing');
                    return;
                }

                // Only handle changes when in replay or paused mode
                if (this.mode === 'replay' || this.mode === 'paused') {
                    // Skip if granularity and chartType haven't actually changed
                    if (granularity === previous?.granularity && chartType === previous?.chartType) {
                        console.log('[ReplayController] Settings unchanged, skipping rebuild');
                        return;
                    }
                    
                    console.log(
                        '[ReplayController] Settings changed during replay - granularity:',
                        granularity,
                        'chartType:',
                        chartType,
                        '(previous granularity:',
                        previous?.granularity,
                        'previous chartType:',
                        previous?.chartType,
                        ')'
                    );
                    if (this.currentEpoch) {
                        console.log('[ReplayController] Current epoch:', new Date(this.currentEpoch));
                    }

                    // Rebuild chart data with new granularity from current position
                    if (this.currentEpoch && this.mainStore.chart.currentActiveSymbol) {
                        const wasPlaying = this.isPlaying;
                        if (wasPlaying) {
                            this.pause();
                        }

                        // Show loading indicator during granularity change
                        this.mainStore.loader.show();
                        this.mainStore.state.setChartIsReady(false);

                        try {
                            // Cleaining up the previous chart data
                            const { eventLog } = await import('src/replay/EventLog');
                            eventLog.clear();

                            // Fetching the new data with the new granularity
                            await this._fetchHistoricalDataForReplay(
                                this.mainStore.chart.currentActiveSymbol?.symbol,
                                this.replayStartEpoch ?? this.currentEpoch
                            );

                        // Get all events from start to current position
                        const events = eventStream.getTimeWindow(
                            this.replayStartEpoch ?? this.currentEpoch,
                            this.currentEpoch
                        );
                        console.log(
                            '[ReplayController] Rebuilding chart with',
                            events.length,
                            'events from',
                            new Date(this.replayStartEpoch ?? this.currentEpoch),
                            'to',
                            new Date(this.currentEpoch)
                        );

                        const granularityMs = this.mainStore.chartAdapter.getGranularityInMs();
                        
                        // Preserve user's chart type selection
                        // Get the actual chart type from chartTypeStore, not state.chartType
                        const currentChartType = this.mainStore.chartType?.type?.id;
                        const effectiveChartType =
                            currentChartType ||
                            this.mainStore.state.chartType ||
                            chartType ||
                            previous?.chartType ||
                            (granularityMs === 0 ? 'line' : 'candles');

                        // Invalidate the segment iterator as underlying data has changed
                        this.segmentIterator = undefined;

                        console.log(
                            '[ReplayController] Updating chart with new granularity:',
                            granularityMs,
                            'chartType:',
                            effectiveChartType,
                            '(currentChartType:',
                            currentChartType,
                            'state.chartType:',
                            this.mainStore.state.chartType,
                            'prev:',
                            previous?.chartType,
                            ')'
                        );

                        // Call newChart on the chartAdapter to update Flutter chart's internal granularity
                        // This is similar to how live mode handles granularity changes
                        await this.mainStore.chartAdapter.newChart();

                        // Apply all events up to current position with new settings
                        // applyReplayBatch with append=false will replace all data via feed.onTickHistory
                        if (events.length > 0) {
                            const quotes = events.map(evt => evt.raw);
                            console.log('[ReplayController] Applying', quotes.length, 'quotes with new granularity');
                            console.log('[ReplayController] First quote OHLC:', quotes[0]?.Open, quotes[0]?.High, quotes[0]?.Low, quotes[0]?.Close);
                            console.log('[ReplayController] First quote raw:', quotes[0]);
                            await this.mainStore.chartAdapter.applyReplayBatch(quotes, false); // Use false to replace data, not append

                            // Set cursor time after applying data
                            if (this.mainStore.chartAdapter.flutterChart?.replay) {
                                this.mainStore.chartAdapter.flutterChart.replay.setCursorTime(this.currentEpoch);
                            }

                            // Scroll to the current replay position after granularity change
                            this.mainStore.chartAdapter.markProgrammaticScroll();
                            this.mainStore.chartAdapter.flutterChart?.app.scrollToLastTick();

                            // Update currentEpoch to the last event we just applied
                            // and reset currentEventIndex so the first playback batch
                            // uses replace mode (appendMode=false) instead of appending
                            // a duplicate candle on top of the rebuilt data.
                            const lastEvent = events[events.length - 1];
                            runInAction(() => {
                                this.currentEpoch = lastEvent.epoch;
                                this.currentEventIndex = 0;
                            });
                        }

                        // Resume playback if it was playing
                        if (wasPlaying) {
                            console.log('[ReplayController] Resuming playback after rebuild');
                            runInAction(() => {
                                this.isPlaying = true;
                                this.mode = 'replay';
                            });
                            // Use setTimeout to ensure state updates are processed before starting playback
                            setTimeout(() => {
                                console.log('[ReplayController] Starting playback loop, state:', {
                                    isPlaying: this.isPlaying,
                                    mode: this.mode,
                                    currentEpoch: this.currentEpoch ? new Date(this.currentEpoch) : null,
                                    replayEndEpoch: this.replayEndEpoch ? new Date(this.replayEndEpoch) : null,
                                });
                                this._startPlaybackLoop();
                            }, 0);
                        }
                        
                        } catch (error) {
                            console.error('[ReplayController] Error during granularity change:', error);
                        } finally {
                            // Hide loading indicator and mark chart as ready
                            this.mainStore.loader.hide();
                            this.mainStore.state.setChartIsReady(true);
                        }
                    }
                }
            },
            { delay: 100 } // Small delay to batch rapid changes
        );
    }

    /**
     * Progress percentage (0-100) through current replay.
     */
    get progress(): number {
        if (!this.replayStartEpoch || !this.currentEpoch || !this.replayEndEpoch) {
            return 0;
        }

        const total = this.replayEndEpoch - this.replayStartEpoch;
        const current = this.currentEpoch - this.replayStartEpoch;

        return Math.min(100, Math.max(0, (current / total) * 100));
    }

    /**
     * Can step forward (more events available).
     */
    get canStepForward(): boolean {
        if (this.mode !== 'replay' || !this.currentEpoch) return false;
        const bounds = eventStream.getBounds();
        return this.currentEpoch < (bounds.last ?? 0);
    }

    /**
     * Can step backward (not at start).
     */
    get canStepBack(): boolean {
        if (this.mode !== 'replay' || !this.currentEpoch) return false;
        const bounds = eventStream.getBounds();
        return this.currentEpoch > (bounds.first ?? 0);
    }

    /**
     * Get available time range from event stream.
     */
    get availableTimeRange(): { first: Date | null; last: Date | null } {
        const bounds = eventStream.getBounds();
        return {
            first: bounds.first ? new Date(bounds.first) : null,
            last: bounds.last ? new Date(bounds.last) : null,
        };
    }

    /**
     * Rewind to a specific epoch and enter replay mode.
     */
    async rewindTo(targetEpoch: number) {
        console.log('[ReplayController] ===== rewindTo() called =====');
        console.log('[ReplayController] rewindTo target:', new Date(targetEpoch));
        console.log('[ReplayController] Current state before rewind:', {
            mode: this.mode,
            currentEpoch: this.currentEpoch ? new Date(this.currentEpoch) : null,
            progress: this.progress.toFixed(2) + '%',
            currentIndex: this.currentEventIndex,
        });
        console.trace('[ReplayController] rewindTo() call stack');

        // Require an active symbol; otherwise do not alter chart state
        const symbol = this.mainStore.chart.currentActiveSymbol;
        if (!symbol) {
            console.warn('[ReplayController] rewindTo: No active symbol');
            this.mainStore.notifier.notify({ text: 'No active symbol to replay', type: 'error', category: 'replay' });
            return;
        }
        this.mainStore.eventRecorder.recordAction('rewind', { targetEpoch });
        this.mainStore.replayDebug.logAction('rewind', { targetEpoch });

        // Pause any ongoing playback but avoid clearing chart until we validate data
        this.stop();

        // Unsubscribe from live feed to prevent interference
        console.log(
            '[ReplayController] Unsubscribing from live feed:',
            symbol.symbol,
            'granularity:',
            this.mainStore.state.effectiveApiGranularity
        );
        this.mainStore.chart.feed?.unsubscribe({
            symbol: symbol.symbol,
            granularity: this.mainStore.state.effectiveApiGranularity,
        });
        console.log('[ReplayController] Unsubscribed from live feed');

        // Check if we have events around target epoch
        const bounds = eventStream.getBounds();
        console.log('[ReplayController] EventStream bounds:', bounds);

        // If no events OR target is outside bounds, fetch historical data
        const needsHistoricalFetch =
            !bounds.first ||
            targetEpoch < bounds.first - 3600000 || // Target is >1hr before earliest
            targetEpoch > (bounds.last ?? 0); // Target is after latest

        if (needsHistoricalFetch) {
            console.log('[ReplayController] ⚠️  NEEDS HISTORICAL DATA FETCH');
            console.log(
                '[ReplayController] Target epoch:',
                new Date(targetEpoch).toLocaleString(),
                '(',
                targetEpoch,
                ')'
            );
            console.log('[ReplayController] Bounds:', {
                first: bounds.first ? new Date(bounds.first).toLocaleString() : 'none',
                last: bounds.last ? new Date(bounds.last).toLocaleString() : 'none',
            });
            const fetchStartTime = performance.now();
            await this._fetchHistoricalDataForReplay(symbol.symbol, targetEpoch);
            const fetchElapsed = performance.now() - fetchStartTime;
            console.log('[ReplayController] ✅ Historical data fetch completed in', fetchElapsed.toFixed(0), 'ms');
        }

        console.log('[ReplayController] Getting updated bounds from eventStream...');
        const boundsStartTime = performance.now();
        const updatedBounds = eventStream.getBounds();
        const boundsElapsed = performance.now() - boundsStartTime;
        console.log('[ReplayController] getBounds() took', boundsElapsed.toFixed(0), 'ms, result:', updatedBounds);
        console.log(
            '[ReplayController] Bounds: first=',
            new Date(updatedBounds.first ?? 0),
            'last=',
            new Date(updatedBounds.last ?? 0)
        );
        console.log('[ReplayController] Requested targetEpoch:', new Date(targetEpoch));

        if (!updatedBounds.first) {
            console.warn('[ReplayController] No events in eventStream - cannot replay');
            this.mainStore.notifier.notify({
                text: 'No historical data available at the selected time',
                type: 'warning',
                category: 'replay',
            });
            // Return to live safely
            this.jumpToLive();
            return;
        }

        // Use user's selected targetEpoch as the playback start, regardless of data availability
        // This ensures replay starts from the selected time, even if data doesn't exist there yet
        const effectiveStart = targetEpoch;
        // Always end at current time (Date.now()) for 24h and year-long replays
        // This allows streaming to continue loading future data during playback
        const effectiveEnd = Date.now();

        // Warn if data is not available at the selected start time
        const firstDataEpoch = updatedBounds.first ?? targetEpoch;
        if (effectiveStart < firstDataEpoch) {
            console.warn(
                '[ReplayController] Requested start time earlier than available data. Will start playback from selected time and display data once it becomes available. Selected:',
                new Date(effectiveStart),
                'First available:',
                new Date(firstDataEpoch)
            );
        }

        console.log('[ReplayController] Getting time window from eventStream...');
        const windowStartTime = performance.now();
        // Load initial window from firstDataEpoch onwards (to display actual data on chart)
        const firstDataDisplayEpoch = Math.max(effectiveStart, firstDataEpoch);
        const events = eventStream.getTimeWindow(firstDataDisplayEpoch, effectiveEnd);
        const windowElapsed = performance.now() - windowStartTime;
        console.log(
            '[ReplayController] getTimeWindow() took',
            windowElapsed.toFixed(0),
            'ms, found',
            events.length,
            'events from',
            new Date(firstDataDisplayEpoch),
            'to',
            new Date(effectiveEnd)
        );

        runInAction(() => {
            this.mode = 'replay';
            this.currentEpoch = effectiveStart; // Start from user's selected time
            this.isPlaying = false;
            this.currentEventIndex = 0;
            this.replayStartEpoch = effectiveStart; // Preserve user's selected start time
            this.replayEndEpoch = effectiveEnd; // End at latest available data
            this.lastHistoricalDataEpoch = effectiveEnd; // Track actual last data point (same as replayEndEpoch)
            console.log('[ReplayController] ═══════════════════════════════════════════════════════════');
            console.log('[ReplayController] ===== MODE CHANGED TO REPLAY =====');
            console.log('[ReplayController] 🎬 REPLAY START EPOCH:', effectiveStart);
            console.log('[ReplayController] 🎬 REPLAY START TIME:', new Date(effectiveStart).toLocaleString());
            console.log('[ReplayController] 🏁 REPLAY END EPOCH:', this.replayEndEpoch);
            console.log('[ReplayController] 🏁 REPLAY END TIME:', new Date(this.replayEndEpoch).toLocaleString());
            console.log(
                '[ReplayController] 📊 Duration (days):',
                Math.round((this.replayEndEpoch - effectiveStart) / (1000 * 60 * 60 * 24))
            );
            console.log('[ReplayController] ═══════════════════════════════════════════════════════════');
            console.log(
                '[ReplayController] Reset currentEventIndex to 0, currentEpoch will be set by _resetChartToEpoch'
            );
        });

        // Clear current chart and rebuild from historical data
        // Use replayStartEpoch if it was adjusted due to gap detection during chunk fetch
        const actualStartEpoch = this.replayStartEpoch || effectiveStart;
        if (actualStartEpoch !== effectiveStart) {
            console.log(
                '[ReplayController] Using adjusted start epoch for chart reset:',
                new Date(actualStartEpoch),
                'instead of requested:',
                new Date(effectiveStart)
            );
        }
        await this._resetChartToEpoch(actualStartEpoch);
    }

    /**
     * Start playback from current epoch (or specified epoch).
     */
    async play(fromEpoch?: number, speed: TPlaybackSpeed = 1) {
        // Log entry with full context
        console.log('[ReplayController] ===== play() called =====');
        console.log('[ReplayController] play() params:', {
            fromEpoch: fromEpoch ? new Date(fromEpoch) : undefined,
            speed,
            currentMode: this.mode,
            currentEpoch: this.currentEpoch ? new Date(this.currentEpoch) : null,
            isPlaying: this.isPlaying,
            currentIndex: this.currentEventIndex,
            progress: this.progress.toFixed(2) + '%',
        });
        console.trace('[ReplayController] play() call stack');

        // FAST RESUME PATH: If we are simply resuming from a paused state, skip all
        // heavy initialisation (loading overlay, live-status update, data fetching,
        // chart resets). Just flip the flags and restart the playback timer.
        if (this.mode === 'paused' && this.currentEpoch) {
            // When fromEpoch is not provided, always fast-resume.
            // When fromEpoch IS provided but we are already at or past that point,
            // also fast-resume (no need to reload).
            const canFastResume = !fromEpoch || (this.currentEpoch >= fromEpoch);

            if (canFastResume) {
                console.log(
                    '[ReplayController] ✓ Fast-resuming from paused state at:',
                    new Date(this.currentEpoch),
                    'progress:',
                    this.progress.toFixed(2) + '%'
                );
                runInAction(() => {
                    this.mode = 'replay';
                    this.isPlaying = true;
                    this.playbackSpeed = speed;
                });
                this._startPlaybackLoop();
                return;
            }
        }

        // Set flag to skip the granularity/chartType reaction during initialization
        this.isInitializingReplay = true;
        runInAction(() => {
            this.isLoadingReplay = true;
            this.mainStore.chartAdapter.replayFeedCount = 0;
        });

        // Save pre-replay settings so we can restore them when exiting replay
        this.preReplayGranularity = this.mainStore.state.granularity;
        this.preReplayChartType = this.mainStore.state.chartType;
        console.log('[ReplayController] Saved pre-replay state:', {
            granularity: this.preReplayGranularity,
            chartType: this.preReplayChartType,
        });

        // BACKTESTING RULE: Tick granularity is not supported in replay mode
        // If tick (granularity 0) is currently selected, auto-switch to 1-second update interval.
        // This sets state.granularity to 1 (1-second display interval) while the actual
        // candle granularity for API/Flutter remains 60s (enforced by REPLAY_DEFAULT_GRANULARITY).
        const currentGranularity = this.mainStore.state.granularity;
        const needsGranularitySwitch = currentGranularity === 0;

        if (needsGranularitySwitch) {
            console.log('[ReplayController] Tick granularity detected, auto-switching to 1-second update interval for replay mode');
            
            // IMPORTANT: Set mode to 'paused' BEFORE changing granularity to prevent
            // the ChartStore.newChart() from being triggered by the granularity change reaction.
            // This avoids a race condition where newChart() starts a new subscription that
            // reinitializes the chart and wipes replay data.
            runInAction(() => {
                this.mode = 'paused';
                console.log('[ReplayController] Mode set to paused BEFORE granularity change');
                this.mainStore.state.granularity = 1;
            });
            
            // Flutter still needs the effective candle granularity (60s), not the display interval (1s)
            await this.mainStore.chartAdapter.updateGranularity(60000); // 60 seconds in milliseconds
        }
        // NOTE: Chart type switch to 'candles' is deferred until after all resets
        // (resetSeries, onTickHistory, resetToEpoch) so Flutter's style isn't wiped.

        // IMPORTANT: Clear chart data BEFORE setting isLive=false!
        // When isLive becomes false, the Flutter widget rebuilds and looks for currentReplayEpoch.
        // If we don't clear the old ticks first, the widget will try to find a tick in the wrong (live) data.
        if (fromEpoch && this.mainStore.chartAdapter.flutterChart?.replay) {
            console.log('[ReplayController] Clearing chart data before isLive=false');
            this.mainStore.chartAdapter.flutterChart.replay.resetSeries();
            // Small delay to ensure Flutter processes the reset before we set isLive=false
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Now set the replay cursor time
            console.log('[ReplayController] Pre-setting replay cursor time before isLive=false:', new Date(fromEpoch));
            this.mainStore.chartAdapter.flutterChart.replay.setCursorTime(fromEpoch);
        }

        // Set Flutter chart to non-live mode for replay
        // This ensures the current tick indicator displays correctly (without the live-only styling)
        await this.mainStore.chartAdapter.updateLiveStatus(false);

        // NOTE: isInitializingReplay flag stays TRUE until after setChartType('candles') below.
        // This prevents the MobX reaction from triggering a redundant data reload when
        // chartType changes from undefined → 'candles' during the initial play() setup.

        try {
            // VALIDATION: Check if the user selected a future date
            // Replay can only work with historical data - we cannot replay the future!
            if (fromEpoch) {
                const now = Date.now();
                // Allow a small buffer (5 minutes) to account for any clock differences
                const bufferMs = 5 * 60 * 1000;
                
                if (fromEpoch > now + bufferMs) {
                    console.error('[ReplayController] ❌ Cannot start replay from a FUTURE date!');
                    console.error('[ReplayController] Selected time:', new Date(fromEpoch).toLocaleString());
                    console.error('[ReplayController] Current time:', new Date(now).toLocaleString());
                    console.error('[ReplayController] Difference:', ((fromEpoch - now) / 60000).toFixed(1), 'minutes in the future');
                    
                    this.mainStore.notifier.notify({
                        text: 'Cannot start replay from a future date. Please select a past date and time.',
                        type: 'error',
                        category: 'replay',
                    });
                    
                    return;
                }
            }

            // If already in replay or paused mode, resume (don't restart)
            // Allow resuming even if fromEpoch is provided, as long as we're already at or past that point
            if ((this.mode === 'replay' || this.mode === 'paused') && this.currentEpoch) {
                // If fromEpoch is not provided, always resume
                if (!fromEpoch) {
                    console.log(
                        '[ReplayController] ✓ Already in replay/paused mode, resuming from:',
                        new Date(this.currentEpoch),
                        'at progress:',
                        this.progress.toFixed(2) + '%'
                    );
                    runInAction(() => {
                        this.mode = 'replay';
                        this.isPlaying = true;
                        this.playbackSpeed = speed;
                    });
                    this._startPlaybackLoop();
                    return;
                }

                // If fromEpoch is provided, check if it's close to our current position
                // If we're already at or past the fromEpoch, resume instead of restarting
                const currentEpoch = this.currentEpoch;
                const epochDifference = currentEpoch - fromEpoch;
                const isAlreadyPastStart = epochDifference >= 0;

                if (isAlreadyPastStart) {
                    console.log(
                        '[ReplayController] ✓ Already in replay/paused mode and past requested start point, resuming'
                    );
                    console.log(
                        '[ReplayController] Current:',
                        new Date(currentEpoch),
                        'at progress:',
                        this.progress.toFixed(2) + '%'
                    );
                    console.log(
                        '[ReplayController] Requested start:',
                        new Date(fromEpoch),
                        '(difference:',
                        (epochDifference / 60000).toFixed(1),
                        'minutes)'
                    );
                    runInAction(() => {
                        this.mode = 'replay';
                        this.isPlaying = true;
                        this.playbackSpeed = speed;
                    });
                    this._startPlaybackLoop();
                    return;
                }

                // If fromEpoch is AFTER our current position, we need to restart
                console.warn(
                    '[ReplayController] ⚠️ Requested start',
                    new Date(fromEpoch),
                    'is AFTER current position',
                    new Date(currentEpoch)
                );
                console.warn(
                    '[ReplayController] This will RESTART the replay. Progress:',
                    this.progress.toFixed(2) + '%'
                );
            }

            // If we reach here with fromEpoch while already in replay mode, it means we need to restart

            if (fromEpoch) {
                // For fresh backtest start: Don't call rewindTo as it loads historical data
                // Instead, set up replay state directly and start playing forward
                console.log('[ReplayController] Setting up fresh replay from epoch:', new Date(fromEpoch));

                // Unsubscribe from live feed first
                const symbol = this.mainStore.chart.currentActiveSymbol;
                if (symbol) {
                    console.log('[ReplayController] Unsubscribing from live feed:', symbol.symbol);
                    this.mainStore.chart.feed?.unsubscribe({
                        symbol: symbol.symbol,
                        granularity: this.mainStore.state.effectiveApiGranularity,
                    });
                }

                // Fetch historical data if needed (but don't load it to chart yet)
                const bounds = eventStream.getBounds();
                const needsHistoricalFetch =
                    !bounds.first || fromEpoch < bounds.first - 3600000 || fromEpoch > (bounds.last ?? 0);

                if (needsHistoricalFetch) {
                    console.log('[ReplayController] Fetching historical data for epoch:', fromEpoch);
                    await this._fetchHistoricalDataForReplay(symbol!.symbol, fromEpoch);

                    // Verify that data was actually fetched
                    const boundsAfterFetch = eventStream.getBounds();
                    if (!boundsAfterFetch.first) {
                        console.error('[ReplayController] No historical data available after fetch');
                        this.mainStore.notifier.notify({
                            text: 'No historical data available for the selected date',
                            type: 'error',
                            category: 'replay',
                        });

                        // Resubscribe to live feed since replay failed
                        if (symbol) {
                            this.mainStore.chart.refreshChart();
                        }

                        runInAction(() => {
                            this.mode = 'live';
                            this.isPlaying = false;
                        });
                        return;
                    }

                    console.log(
                        '[ReplayController] Historical data fetched successfully. Bounds:',
                        new Date(boundsAfterFetch.first),
                        'to',
                        new Date(boundsAfterFetch.last ?? 0)
                    );
                }

                // Clear the Flutter chart completely to ensure clean slate
                console.log('[ReplayController] Clearing Flutter chart for fresh replay start');
                if (this.mainStore.chartAdapter.flutterChart?.replay) {
                    this.mainStore.chartAdapter.flutterChart.replay.resetSeries();
                    this.mainStore.chartAdapter.flutterChart.replay.clearReplayState();
                }

                // Clear all existing chart data
                if (this.mainStore.chart.feed) {
                    this.mainStore.chart.feed.quotes = [];
                }

                // Initialize feed with empty data to prepare for replay
                console.log('[ReplayController] Initializing feed with empty history');
                await this.mainStore.chartAdapter.onTickHistory([]);

                // Reset chart to empty state at start epoch
                await this.mainStore.chartAdapter.resetToEpoch(fromEpoch);

                console.log('[ReplayController] Chart reset to empty state at epoch:', new Date(fromEpoch));

                // BACKTESTING RULE: Only candles data is supported.
                // Apply chart type AFTER all resets (resetSeries, onTickHistory, resetToEpoch)
                // so the style isn't overwritten by Flutter's internal state resets.
                // Use setChartType for atomic JS state + Flutter update, same as live mode.
                // Wrap in runInAction to comply with MobX strict mode.
                runInAction(() => {
                    this.mainStore.state.setChartType('candles');
                });
                // Use effective granularity (>= 60) for Flutter, not the display interval
                const effectiveGran = Math.max(this.mainStore.state.granularity || 60, ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY);
                await this.mainStore.chartAdapter.updateGranularity(
                    effectiveGran * 1000
                );
                console.log('[ReplayController] Applied candles chart type after chart reset');

                // NOW clear the initialization flag - all settings changes are done
                this.isInitializingReplay = false;
                this._clearLoadingAfterRender();

                // Set replay state
                runInAction(() => {
                    this.mode = 'replay';
                    this.isPlaying = true;
                    this.playbackSpeed = speed;
                    this.currentEpoch = fromEpoch;
                    this.replayStartEpoch = fromEpoch;
                    this.replayEndEpoch = Date.now();
                    this.currentEventIndex = 0;
                    this.totalEvents = 0;
                    console.log('[ReplayController] Replay state initialized, starting playback');
                });

                // Start playback immediately
                this._startPlaybackLoop();
            } else if (this.mode === 'live') {
                // If starting play from live mode, unsubscribe first
                const symbol = this.mainStore.chart.currentActiveSymbol;
                if (symbol) {
                    console.log('[ReplayController] Play: Unsubscribing from live feed:', symbol.symbol);
                    this.mainStore.chart.feed?.unsubscribe({
                        symbol: symbol.symbol,
                        granularity: this.mainStore.state.effectiveApiGranularity,
                    });
                    console.log('[ReplayController] Play: Unsubscribed from live feed');
                }

                runInAction(() => {
                    this.mode = 'replay';
                    this.isPlaying = true;
                    this.playbackSpeed = speed;
                    console.log('[ReplayController] play(): Mode set to REPLAY from LIVE');
                });

                // Start playback for live->replay transition
                this.mainStore.eventRecorder.recordAction('play', { fromEpoch: this.currentEpoch, speed });
                this.mainStore.replayDebug.logAction('play', { fromEpoch: this.currentEpoch, speed });

                // Ensure replay bounds are set
                runInAction(() => {
                    if (!this.replayStartEpoch && this.currentEpoch) {
                        this.replayStartEpoch = this.currentEpoch;
                    }

                    if (!this.replayEndEpoch) {
                        const bounds = eventStream.getBounds();
                        if (bounds.last) {
                            this.replayEndEpoch = bounds.last;
                        }
                    }
                });

                this._startPlaybackLoop();
            } else {
                // Resuming from paused state
                this.mainStore.eventRecorder.recordAction('play', { fromEpoch: this.currentEpoch, speed });
                this.mainStore.replayDebug.logAction('play', { fromEpoch: this.currentEpoch, speed });

                runInAction(() => {
                    this.isPlaying = true;
                    this.playbackSpeed = speed;
                });

                this._startPlaybackLoop();
            }
        } catch (err) {
            console.error('[ReplayController] play() error:', err);
            runInAction(() => {
                this.isPlaying = false;
                this.mode = 'paused';
            });
        } finally {
            // Always clear the initialization flag to avoid permanently blocking the reaction
            this.isInitializingReplay = false;
            // If still loading (error path), clear after render or with safety timeout
            if (this.isLoadingReplay) {
                this._clearLoadingAfterRender();
            }
        }
    }

    /**
     * Delay clearing isLoadingReplay until the Flutter chart has actually received
     * and rendered replay data. This keeps the loader overlay visible over the
     * "giant red candle" that appears during the initial render cycle.
     */
    private _clearLoadingAfterRender() {
        const adapter = this.mainStore.chartAdapter;
        // Snapshot the feed count at the time we start waiting
        const feedCountAtStart = adapter.replayFeedCount;

        const checkInterval = setInterval(() => {
            // Wait until at least 3 replay batches have been fed since we started waiting
            if (adapter.isChartLoaded && adapter.replayFeedCount >= feedCountAtStart + 3) {
                clearInterval(checkInterval);
                // 800ms buffer for Flutter paint cycle to fully complete
                setTimeout(() => {
                    runInAction(() => {
                        this.isLoadingReplay = false;
                    });
                    console.log('[ReplayController] Loader dismissed after render confirmation (feedCount:', adapter.replayFeedCount, ')');
                }, 800);
            }
        }, 100);

        // Safety timeout — always clear after 8 seconds max to avoid stuck loader
        setTimeout(() => {
            clearInterval(checkInterval);
            if (this.isLoadingReplay) {
                runInAction(() => {
                    this.isLoadingReplay = false;
                });
                console.warn('[ReplayController] Loader dismissed by safety timeout');
            }
        }, 8000);
    }

    /**
     * Pause playback at current position.
     */
    pause() {
        runInAction(() => {
            this.isPlaying = false;
            this.mode = 'paused';
        });
        this.mainStore.eventRecorder.recordAction('pause', { epoch: this.currentEpoch });
        this.mainStore.replayDebug.logAction('pause', { epoch: this.currentEpoch });

        this._stopPlaybackLoop();
    }

    /**
     * Stop playback and return to live mode.
     * Also cancels any ongoing chunked API fetches and clears partial data.
     */
    async stop() {
        this._stopPlaybackLoop();

        // Set mode to 'live' SYNCHRONOUSLY first so callers that don't await
        // (e.g. changeSymbol) see the correct state immediately and proceed
        // to reload the chart instead of skipping due to replay-mode guard.
        runInAction(() => {
            this.mode = 'live';
            this.isPlaying = false;
        });

        // Abort any ongoing chunked API fetch
        const wasAborted = !!this.chunkFetchAbortController;
        if (this.chunkFetchAbortController) {
            console.log('[ReplayController] Aborting ongoing chunk fetch');
            this.chunkFetchAbortController.abort();
            this.chunkFetchAbortController = undefined;
        }

        // Clean up SegmentIterator to free memory
        if (this.segmentIterator) {
            console.log('[ReplayController] Cleaning up SegmentIterator');
            // Trim buffer to minimal size to free memory
            this.segmentIterator.trimBuffer(1000);
            this.segmentIterator = undefined;
        }

        // If we aborted a chunked fetch, clean up partial data
        if (wasAborted) {
            // Clear EventLog to remove partial replay data
            try {
                const { eventLog } = await import('src/replay/EventLog');
                if (eventLog) {
                    eventLog.clear();
                    console.log('[ReplayController] EventLog cleared due to abort');
                }
            } catch (err) {
                console.warn('[ReplayController] Failed to clear EventLog:', err);
            }

            // Clear pending segments from ingestion pipeline
            try {
                const { segmentIngestionPipeline } = await import('src/replay/SegmentIngestionPipeline');
                segmentIngestionPipeline.clearPendingSegments();
            } catch (err) {
                console.warn('[ReplayController] Failed to clear pending segments:', err);
            }

            // Reset streaming progress
            runInAction(() => {
                this.streamingProgress.phase = 'idle';
                this.streamingProgress.chunksLoaded = 0;
                this.streamingProgress.totalChunks = 0;
                this.streamingProgress.initialWindowLoaded = false;
            });
        }

        this.mainStore.eventRecorder.recordAction('stop', { epoch: this.currentEpoch });
        this.mainStore.replayDebug.logAction('stop', { epoch: this.currentEpoch });

        // Clear Flutter replay state (e.g. currentReplayEpoch) so the chart
        // doesn't retain stale replay markers when switching back to live.
        if (this.mainStore.chartAdapter.flutterChart?.replay) {
            this.mainStore.chartAdapter.flutterChart.replay.clearReplayState();
        }

        // Restore pre-replay granularity and chart type (e.g. ticks/line) before
        // going live so the chart reloads with the correct settings.
        const savedGranularity = this.preReplayGranularity;
        const savedChartType = this.preReplayChartType;
        if (savedGranularity !== undefined) {
            console.log('[ReplayController] Restoring pre-replay granularity:', savedGranularity);
            this.mainStore.state.granularity = savedGranularity;
            this.mainStore.chart.granularity = savedGranularity;
            // Notify Flutter of the granularity change
            this.mainStore.chartAdapter.updateGranularity(
                savedGranularity === 0 ? 0 : savedGranularity * 1000
            );
        }
        if (savedChartType) {
            console.log('[ReplayController] Restoring pre-replay chartType:', savedChartType);
            this.mainStore.state.setChartType(savedChartType);
        }
        this.preReplayGranularity = undefined;
        this.preReplayChartType = undefined;

        // Restore Flutter chart to live mode
        await this.mainStore.chartAdapter.updateLiveStatus(true);

        runInAction(() => {
            this.currentEpoch = null;
            this.replayStartEpoch = null;
            this.replayEndEpoch = null;
            this.lastHistoricalDataEpoch = null;
            this.currentEventIndex = 0;
            this.totalEvents = 0;
        });
    }

    /**
     * Step forward one event.
     */
    async stepForward() {
        if (!this.canStepForward || !this.currentEpoch) return;

        const nextEvent = eventStream.findClosest(this.currentEpoch + 1);
        if (!nextEvent) return;

        runInAction(() => {
            this.currentEpoch = nextEvent.epoch;
            this.currentEventIndex++;
        });
        this.mainStore.eventRecorder.recordAction('step_forward', { epoch: this.currentEpoch });
        this.mainStore.replayDebug.logAction('step_forward', { epoch: this.currentEpoch });

        await this._applyEvent(nextEvent);
    }

    /**
     * Step backward one event.
     */
    async stepBack() {
        if (!this.canStepBack || !this.currentEpoch) return;

        // Find previous event
        const events = eventStream.getTimeWindow(eventStream.getBounds().first ?? 0, this.currentEpoch - 1);

        const prevEvent = events[events.length - 1];
        if (!prevEvent) return;

        runInAction(() => {
            this.currentEpoch = prevEvent.epoch;
            this.currentEventIndex--;
        });
        this.mainStore.eventRecorder.recordAction('step_back', { epoch: this.currentEpoch });
        this.mainStore.replayDebug.logAction('step_back', { epoch: this.currentEpoch });

        // Rebuild chart up to previous event
        await this._resetChartToEpoch(prevEvent.epoch);
    }

    /**
     * Set playback speed multiplier.
     */
    setSpeed(speed: TPlaybackSpeed) {
        runInAction(() => {
            this.playbackSpeed = speed;
        });
        this.scheduler.setSpeed(speed);
        this.mainStore.eventRecorder.recordAction('speed_change', { speed });
        this.mainStore.replayDebug.logAction('speed_change', { speed });

        // Restart playback loop if playing to apply new speed
        if (this.isPlaying) {
            this._stopPlaybackLoop();
            this._startPlaybackLoop();
        }
    }

    /**
     * Jump back to live mode.
     */
    jumpToLive() {
        console.log('[ReplayController] Jumping to live mode');
        this.stop();

        // Resubscribe to live feed
        const symbol = this.mainStore.chart.currentActiveSymbol;
        if (symbol) {
            console.log('[ReplayController] Resubscribing to live feed via ChartStore.refreshChart');
            // Refresh chart triggers fetchInitialData + live subscription
            this.mainStore.chart.refreshChart();
        }

        this.mainStore.eventRecorder.recordAction('jump_live');
        this.mainStore.replayDebug.logAction('jump_live');
    }

    /**
     * Set replay range (start/end epochs).
     */
    setReplayRange(startEpoch: number, endEpoch: number) {
        runInAction(() => {
            this.replayStartEpoch = startEpoch;
            this.replayEndEpoch = endEpoch;

            const events = eventStream.getTimeWindow(startEpoch, endEpoch);
            this.totalEvents = events.length;
        });
    }

    /**
     * Fast-forward to target epoch using batch processing with backpressure.
     * Uses segment-based loading for efficient one-year replay support.
     */
    async fastForwardTo(targetEpoch: number): Promise<void> {
        if (!this.currentEpoch || targetEpoch <= this.currentEpoch) {
            console.warn(
                '[ReplayController] Invalid fast-forward: current=',
                this.currentEpoch,
                'target=',
                targetEpoch
            );
            return;
        }

        console.log(
            '[ReplayController] Fast-forwarding from',
            new Date(this.currentEpoch),
            'to',
            new Date(targetEpoch)
        );

        const symbol = this.mainStore.chart.currentActiveSymbol;
        if (!symbol) {
            console.warn('[ReplayController] No active symbol for fast-forward');
            return;
        }

        runInAction(() => {
            this.mode = 'replay';
            this.batchProgress = 0;
        });

        try {
            // Create segment iterator for range (lazy streaming)
            const { createSegmentIterator } = await import('src/replay/SegmentIterator');
            this.segmentIterator = await createSegmentIterator({
                symbol: symbol.symbol,
                startEpoch: this.currentEpoch,
                endEpoch: targetEpoch,
                granularity: this.mainStore.state.effectiveApiGranularity || 0,
                batchSize: 200,
            });

            const total = this.segmentIterator.getRemainingCount();
            console.log('[ReplayController] Fast-forwarding (streaming)', total, 'ticks');

            let processed = 0;
            const maxBatchesPerFrame = 6; // keep frames responsive
            const frameBudgetMs = 12; // < 1 frame at 60fps

            await new Promise<void>((resolve, reject) => {
                const processFrame = async () => {
                    try {
                        const frameStart = performance.now();
                        let batches = 0;

                        while (batches < maxBatchesPerFrame) {
                            const batchTicks = this.segmentIterator!.nextBatch(200);
                            if (!batchTicks.length) {
                                // Done
                                runInAction(() => {
                                    this.currentEpoch = targetEpoch;
                                    this.batchProgress = 100;
                                    this.mode = 'paused';
                                });
                                console.log('[ReplayController] Fast-forward complete');
                                resolve();
                                return;
                            }

                            const quotes: TQuote[] = batchTicks.map(t => ({
                                Date: new Date(t.epoch).toISOString().substring(0, 19),
                                DT: new Date(t.epoch),
                                Close: t.price,
                                Volume: t.volume ?? 1,
                            }));

                            await this.mainStore.chartAdapter.applyReplayBatch(quotes, true);
                            if (quotes.length) {
                                const lastQuote = quotes[quotes.length - 1];
                                this.mainStore.chartAdapter.ensureCursorVisible(lastQuote.DT?.getTime() ?? 0);
                                runInAction(() => {
                                    this.currentEpoch = lastQuote.DT?.getTime() ?? this.currentEpoch;
                                });
                            }

                            processed += batchTicks.length;
                            runInAction(() => {
                                this.batchProgress = total > 0 ? (processed / total) * 100 : 100;
                                this.currentEventIndex = processed;
                            });

                            batches++;
                            if (performance.now() - frameStart >= frameBudgetMs) break;
                        }

                        requestAnimationFrame(processFrame);
                    } catch (e) {
                        console.error('[ReplayController] Fast-forward error:', e);
                        runInAction(() => {
                            this.mode = 'paused';
                        });
                        reject(e);
                    }
                };

                requestAnimationFrame(processFrame);
            });

            // Cleanup
            this.segmentIterator.destroy();
            this.segmentIterator = undefined;
        } catch (error) {
            console.error('[ReplayController] Fast-forward error:', error);
            runInAction(() => {
                this.mode = 'paused';
            });
        }
    }

    /**
     * Get current replay state snapshot.
     */
    getState(): TReplayState {
        return {
            mode: this.mode,
            currentEpoch: this.currentEpoch,
            playbackSpeed: this.playbackSpeed,
            isPlaying: this.isPlaying,
            replayStartEpoch: this.replayStartEpoch,
            replayEndEpoch: this.replayEndEpoch,
            progress: this.progress,
        };
    }

    /**
     * Enable test mode for ultra-fast replay validation.
     * Skips most rendering updates to validate data integrity quickly.
     */
    enableTestMode(skipInterval = 100) {
        runInAction(() => {
            this.testMode = true;
            this.skipInterval = skipInterval;
        });
        console.log(`[ReplayController] Test mode enabled, rendering every ${skipInterval}th event`);
    }

    /**
     * Disable test mode and return to normal rendering.
     */
    disableTestMode() {
        runInAction(() => {
            this.testMode = false;
        });
        console.log('[ReplayController] Test mode disabled');
    }

    /**
     * Skip to specific progress percentage (0-100) instantly.
     * Useful for quickly testing different time ranges.
     */
    async skipToProgress(targetPercent: number) {
        if (!this.replayStartEpoch || !this.replayEndEpoch) {
            console.warn('[ReplayController] Cannot skip: no replay range set');
            return;
        }

        const clampedPercent = Math.max(0, Math.min(100, targetPercent));
        const range = this.replayEndEpoch - this.replayStartEpoch;
        const targetEpoch = this.replayStartEpoch + (range * clampedPercent) / 100;

        console.log(`[ReplayController] Skipping to ${clampedPercent}% (epoch: ${targetEpoch})`);

        await this.rewindTo(targetEpoch);
    }

    /**
     * Run a quick validation test: replay from start to end at maximum speed
     * and return timing statistics.
     */
    async runValidationTest(): Promise<{
        success: boolean;
        duration: number;
        eventsProcessed: number;
        startEpoch: number | null;
        endEpoch: number | null;
        errors: string[];
    }> {
        const errors: string[] = [];
        const startTime = performance.now();

        try {
            if (!this.replayStartEpoch || !this.replayEndEpoch) {
                throw new Error('No replay range available');
            }

            console.log('[ReplayController] Starting validation test...');

            // Enable test mode for speed
            const wasTestMode = this.testMode;
            this.enableTestMode(1000); // Render every 1000th event

            // Set to maximum speed
            const originalSpeed = this.playbackSpeed;
            this.setSpeed(1000);

            // Rewind to start
            await this.rewindTo(this.replayStartEpoch);

            // Play to end
            await this.play();

            // Wait for completion
            await new Promise<void>(resolve => {
                const checkComplete = () => {
                    if (!this.isPlaying || this.progress >= 99.9) {
                        resolve();
                    } else {
                        setTimeout(checkComplete, 100);
                    }
                };
                checkComplete();
            });

            // Restore original settings
            if (!wasTestMode) {
                this.disableTestMode();
            }
            this.setSpeed(originalSpeed);

            const duration = performance.now() - startTime;
            const eventsProcessed = this.currentEventIndex;

            console.log(
                `[ReplayController] Validation test completed in ${duration.toFixed(
                    0
                )}ms, processed ${eventsProcessed} events`
            );

            return {
                success: true,
                duration,
                eventsProcessed,
                startEpoch: this.replayStartEpoch,
                endEpoch: this.replayEndEpoch,
                errors,
            };
        } catch (error) {
            errors.push(error instanceof Error ? error.message : String(error));
            console.error('[ReplayController] Validation test failed:', error);

            return {
                success: false,
                duration: performance.now() - startTime,
                eventsProcessed: this.currentEventIndex,
                startEpoch: this.replayStartEpoch,
                endEpoch: this.replayEndEpoch,
                errors,
            };
        }
    }

    // Private methods

    private _startPlaybackLoop() {
        if (!this.currentEpoch || !this.replayEndEpoch) {
            console.warn(
                '[ReplayController] Cannot start playback: currentEpoch=',
                this.currentEpoch,
                'endEpoch=',
                this.replayEndEpoch
            );
            return;
        }

        // Reset the scheduler state (clear any peeked events from previous runs)
        this.scheduler.reset();

        // Sync the scheduler speed with the current playback speed
        this.scheduler.setSpeed(this.playbackSpeed as PlaybackSpeed);

        // Set the granularity on the scheduler for proper candle-based batching
        // For replay mode, we enforce minimum 60-second granularity
        const stateGranularity = this.mainStore.state.granularity || 0;
        const effectiveGranularity = stateGranularity < ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY 
            ? ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY 
            : stateGranularity;
        this.scheduler.setGranularity(effectiveGranularity);
        // Set the display interval for controlling how fast candles appear.
        // When stateGranularity < effectiveGranularity (e.g., 1s vs 60s), the scheduler
        // uses the display interval as the delay between batches instead of the candle boundary gap.
        this.scheduler.setDisplayInterval(stateGranularity > 0 ? stateGranularity : 1);
        console.log('[ReplayController] Scheduler granularity set to:', effectiveGranularity, 'seconds, display interval:', stateGranularity > 0 ? stateGranularity : 1, 'seconds');

        if (this.useSegmentStreaming) {
            console.log(
                '[ReplayController] Starting STREAMING playback from epoch:',
                this.currentEpoch,
                'to',
                this.replayEndEpoch
            );
            this._startPlaybackLoopStreaming();
        } else {
            console.log(
                '[ReplayController] Starting playback from epoch:',
                this.currentEpoch,
                'to',
                this.replayEndEpoch
            );
            // Create iterator from current position to end
            this.eventIterator = eventStream.iterator({
                startEpoch: this.currentEpoch,
                endEpoch: this.replayEndEpoch,
            });

            // Check if the first event is different from selected time (to show notification)
            const firstEventResult = this.eventIterator.next();
            if (firstEventResult.done) {
                // No events found in the selected date range
                console.error(
                    '[ReplayController] No events found between',
                    new Date(this.currentEpoch),
                    'and',
                    new Date(this.replayEndEpoch)
                );
                this.mainStore.notifier.notify({
                    text: 'No historical data available for the selected date',
                    type: 'error',
                    category: 'replay',
                });

                // Return to live mode
                const symbol = this.mainStore.chart.currentActiveSymbol;
                if (symbol) {
                    this.mainStore.chart.refreshChart();
                }

                runInAction(() => {
                    this.mode = 'live';
                    this.isPlaying = false;
                });
                return;
            }

            const firstEvent = firstEventResult.value;
            // Recreate iterator since we consumed one event
            this.eventIterator = eventStream.iterator({
                startEpoch: this.currentEpoch,
                endEpoch: this.replayEndEpoch,
            });

            // If first available event is more than 1 minute after selected time, show notification
            if (firstEvent.epoch > this.currentEpoch + 60000) {
                const selectedTime = new Date(this.currentEpoch).toLocaleTimeString();
                const actualStartTime = new Date(firstEvent.epoch).toLocaleTimeString();
                console.log(
                    `[ReplayController] No data at selected time ${selectedTime}, starting from first available data at ${actualStartTime}`
                );
                this.mainStore.notifier.notify({
                    text: `No data at ${selectedTime}. Starting from ${actualStartTime}`,
                    type: 'info',
                    category: 'replay',
                });
            }

            this._playNextBatch();
        }
    }

    private _startPlaybackLoopStreaming() {
        if (!this.currentEpoch || !this.replayEndEpoch) {
            console.warn('[ReplayController] Cannot start streaming playback');
            return;
        }

        console.log('[ReplayController] Starting STREAMING playback from EventLog');

        // Create iterator from current position in EventLog
        this.eventIterator = eventStream.iterator({
            startEpoch: this.currentEpoch,
            endEpoch: this.replayEndEpoch,
        });

        this._playNextBatch();
    }

    private _stopPlaybackLoop() {
        if (this.playbackTimer) {
            clearTimeout(this.playbackTimer);
            this.playbackTimer = undefined;
        }

        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = undefined;
        }

        this.eventIterator = undefined;
    }

    private _playNextBatch() {
        if (!this.isPlaying || !this.eventIterator) {
            console.log(
                '[ReplayController] _playNextBatch exit: isPlaying=',
                this.isPlaying,
                'hasIterator=',
                !!this.eventIterator
            );
            return;
        }

        const events = this.scheduler.nextBatch(this.eventIterator);
        console.log('[ReplayController] Next batch:', events.length, 'events');
        
        // Log the actual data interval to diagnose tick vs candle data
        if (events.length > 0 && this.currentEventIndex < 5) {
            // Only log first few batches to avoid spam
            const prevEpoch = this.currentEpoch;
            const currEpoch = events[0].epoch;
            if (prevEpoch) {
                const intervalSec = (currEpoch - prevEpoch) / 1000;
                console.log('[ReplayController] Data interval:', intervalSec, 'seconds between events',
                    '(1 sec = tick data, 60 sec = minute candles)');
            }
        }

        if (!events.length) {
            console.log('[ReplayController] ===== EventLog EXHAUSTED =====');
            console.log('[ReplayController] EventLog exhausted at:', {
                currentIndex: this.currentEventIndex,
                currentEpoch: new Date(this.currentEpoch!),
                progress: this.progress.toFixed(2) + '%',
            });

            // EventLog exhausted - load more from SegmentIterator if available
            if (this.useSegmentStreaming && this.segmentIterator && this.segmentIterator.hasNext()) {
                console.log('[ReplayController] ✓ SegmentIterator has more data, loading...');
                const iteratorState = this.segmentIterator.getState();
                console.log('[ReplayController] SegmentIterator state:', {
                    bufferSize: iteratorState.bufferSize,
                    segmentsLoaded: iteratorState.segmentsLoaded,
                    totalSegments: iteratorState.totalSegments,
                    segmentsRemaining: iteratorState.totalSegments - iteratorState.segmentsLoaded,
                    segmentProgress: `${((iteratorState.segmentsLoaded / iteratorState.totalSegments) * 100).toFixed(
                        1
                    )}%`,
                });

                // Load next batch of ticks from SegmentIterator into EventLog
                this._refillEventLogFromSegmentIterator();
                return;
            }
            // No more data from SegmentIterator - check if we can fetch the next API page
            if (this.lastFetchedPageEndEpoch && this.replayEndEpoch && this.lastFetchedPageEndEpoch < this.replayEndEpoch) {
                console.log('[ReplayController] ✓ lastFetchedPageEndEpoch set, fetching next API page...');
                this._fetchNextPageAndContinue();
                return;
            }
            // No more data from any source
            console.log(
                '[ReplayController] No more events available from EventLog, SegmentIterator, or API — playback complete'
            );
            console.log(
                '[ReplayController] Final position: currentIndex:',
                this.currentEventIndex,
                'currentEpoch:',
                new Date(this.currentEpoch!),
                'progress:',
                this.progress.toFixed(2) + '%'
            );
            runInAction(() => {
                this.isPlaying = false;
                this.mode = 'paused';
            });
            return;
        }

        // IMPORTANT: Detect data gaps but don't log confusing messages that look like restarts
        const firstEventEpoch = events[0].epoch;
        if (this.currentEpoch && this.currentEpoch < firstEventEpoch) {
            const gapMs = firstEventEpoch - this.currentEpoch;
            const gapMinutes = Math.floor(gapMs / 60000);
            if (gapMinutes > 1) {
                console.warn(
                    '[ReplayController] Data gap detected: missing',
                    gapMinutes,
                    'minutes of data from',
                    new Date(this.currentEpoch),
                    'to',
                    new Date(firstEventEpoch)
                );
            }
            runInAction(() => {
                this.currentEpoch = firstEventEpoch;
            });
        }

        // Apply batch via single interop call for efficiency
        this.animationFrameId = requestAnimationFrame(async () => {
            // In test mode, skip most rendering updates for speed
            const shouldRender = !this.testMode || this.currentEventIndex % this.skipInterval === 0;

            if (shouldRender) {
                const quotes: TQuote[] = this.scheduler.toQuotes(events);
                
                // DEBUG: Inspect OHLC data being sent to Flutter
                if (quotes.length > 0) {
                    const q = quotes[0];
                    console.log('[ReplayController] 🔍 QUOTE DATA INSPECTION:', {
                        Date: q.Date,
                        Open: q.Open,
                        High: q.High,
                        Low: q.Low,
                        Close: q.Close,
                        Volume: q.Volume,
                        hasOHLC: q.Open !== undefined && q.High !== undefined && q.Low !== undefined,
                        allSamePrice: q.Open === q.High && q.High === q.Low && q.Low === q.Close,
                    });
                }
                // DEBUG: Also inspect the raw event
                if (events.length > 0) {
                    const evt = events[0];
                    console.log('[ReplayController] 🔍 EVENT DATA INSPECTION:', {
                        epoch: evt.epoch,
                        price: evt.price,
                        ohlc: evt.ohlc,
                        rawOpen: evt.raw?.Open,
                        rawHigh: evt.raw?.High,
                        rawLow: evt.raw?.Low,
                        rawClose: evt.raw?.Close,
                        source: evt.source,
                    });
                }
                
                console.log(
                    '[ReplayController] Applying batch: events=',
                    events.length,
                    'quotes=',
                    quotes.length,
                    'firstEpoch=',
                    new Date(events[0].epoch),
                    'lastEpoch=',
                    new Date(events[events.length - 1].epoch)
                );
                
                // For the first batch (currentEventIndex === 0), use append=false to properly initialize
                // the chart's feed state with isFeedLoaded flag. Subsequent batches use append=true.
                const isFirstBatch = this.currentEventIndex === 0;
                const appendMode = !isFirstBatch;
                console.log('[ReplayController] applyReplayBatch appendMode:', appendMode, '(isFirstBatch:', isFirstBatch, ')');
                await this.mainStore.chartAdapter.applyReplayBatch(quotes, appendMode);

                // On the first batch (replay just started), always scroll to the replay position
                // to ensure the chart shows the replay indicator. After that, scroll if shouldFollowReplay is true.
                console.log(
                    '[ReplayController] Scroll check: isFirstBatch=',
                    isFirstBatch,
                    'shouldFollowReplay=',
                    this.shouldFollowReplay,
                    'currentEventIndex=',
                    this.currentEventIndex
                );
                
                // Always enable following on first batch, then respect shouldFollowReplay preference
                if (isFirstBatch) {
                    // Reset follow state on replay start
                    runInAction(() => {
                        this.shouldFollowReplay = true;
                    });
                    console.log('[ReplayController] First batch detected, scrolling to last tick after delay');
                    setTimeout(() => {
                        this.mainStore.chartAdapter.markProgrammaticScroll();
                        this.mainStore.chartAdapter.flutterChart?.app.scrollToLastTick();
                        console.log('[ReplayController] scrollToLastTick called for first batch');
                    }, 200);
                } else if (this.shouldFollowReplay) {
                    // Keep following the replay cursor
                    console.log('[ReplayController] Following replay cursor, calling scrollToLastTick');
                    this.mainStore.chartAdapter.markProgrammaticScroll();
                    this.mainStore.chartAdapter.flutterChart?.app.scrollToLastTick();
                } else {
                    console.log('[ReplayController] Not following (user scrolled away), skipping scrollToLastTick');
                }
            }

            // Don't force cursor visibility during replay - let user navigate freely
            // The chart will naturally scroll as new data is added without forcing position

            const last = events[events.length - 1];
            runInAction(() => {
                this.currentEpoch = last.epoch;
                this.currentEventIndex += events.length;
            });

            // Check if we need to fetch the next page
            // Fetch when we're within 1000 ticks (or ~500 seconds for 0.5 ticks/sec symbol) of the last fetched page end
            if (this.lastFetchedPageEndEpoch && !this.isFetchingNextPage) {
                const remainingTicks = this.totalEvents - this.currentEventIndex;
                const threshold = 1000; // Fetch next page when we have less than 1000 ticks remaining

                if (remainingTicks < threshold && last.epoch >= this.lastFetchedPageEndEpoch - 60000) {
                    console.log('[ReplayController] Approaching end of current page, fetching next page...');
                    runInAction(() => {
                        this.isFetchingNextPage = true;
                    });

                    // Fetch next page asynchronously (don't block playback)
                    this._fetchNextPage()
                        .then(() => {
                            runInAction(() => {
                                this.isFetchingNextPage = false;
                            });
                        })
                        .catch(err => {
                            console.error('[ReplayController] Failed to fetch next page:', err);
                            runInAction(() => {
                                this.isFetchingNextPage = false;
                            });
                        });
                }
            }

            const progress = this.progress.toFixed(2);
            console.log(
                '[ReplayController] Batch applied:',
                events.length,
                'lastEpoch:',
                new Date(last.epoch),
                'currentIndex:',
                this.currentEventIndex,
                'progress:',
                `${progress}%`
            );

            if (shouldRender) {
                this.mainStore.replayDebug.log(`batch applied (${events.length}) -> ${last.epoch}`);
            }

            // Update scheduler with last event epoch for real-time delay calculation
            this.scheduler.setLastEventEpoch(last.epoch);

            // Frame pacing based on speed
            // For speed 1, compute a real-time delay from the epoch gap to
            // the *next* batch.  The scheduler's peekedEvent already holds
            // the first event of that next batch (consumed during nextBatch()),
            // so we must use peekNextEpoch() rather than consuming the raw
            // iterator — otherwise we'd skip a batch and double the delay.
            let delay: number;
            if (this.playbackSpeed === 1) {
                const nextEpoch = this.scheduler.peekNextEpoch();
                if (nextEpoch !== null) {
                    delay = this.scheduler.getRealtimeDelayMs(last.epoch, nextEpoch);
                    console.log('[ReplayController] Speed 1 delay:', delay, 'ms (next batch epoch:', new Date(nextEpoch).toLocaleTimeString(), ')');
                } else if (this.eventIterator) {
                    // No peeked event means nextBatch exhausted the iterator
                    // without finding a boundary change (tick mode or last batch).
                    // Peek the iterator directly as a fallback.
                    const nextResult = this.eventIterator.next();
                    if (!nextResult.done) {
                        const nextEvent = nextResult.value;
                        delay = this.scheduler.getRealtimeDelayMs(last.epoch, nextEvent.epoch);
                        console.log('[ReplayController] Speed 1 delay (iter peek):', delay, 'ms (next event at', new Date(nextEvent.epoch).toLocaleTimeString(), ')');
                        // Put the event back by creating a new iterator from its position
                        this.eventIterator = eventStream.iterator({
                            startEpoch: nextEvent.epoch,
                            endEpoch: this.replayEndEpoch || undefined,
                        });
                    } else {
                        delay = this.scheduler.getFrameDelayMs();
                    }
                } else {
                    delay = this.scheduler.getFrameDelayMs();
                }
            } else {
                delay = this.scheduler.getFrameDelayMs();
            }

            this.playbackTimer = setTimeout(() => {
                this._playNextBatch();
            }, delay);
        });
    }

    /**
     * Refill EventLog from SegmentIterator during playback.
     * This allows continuous replay through year-long data without blocking.
     */
    private async _refillEventLogFromSegmentIterator() {
        if (!this.segmentIterator || !this.isPlaying) {
            console.log('[ReplayController] _refillEventLogFromSegmentIterator: missing iterator or not playing');
            return;
        }

        try {
            console.log('[ReplayController] ===== Refilling EventLog from SegmentIterator =====');
            console.log('[ReplayController] Current state:', {
                currentEpoch: this.currentEpoch ? new Date(this.currentEpoch) : null,
                currentIndex: this.currentEventIndex,
                progress: this.progress.toFixed(2) + '%',
            });
            const startTime = performance.now();

            // Load a batch of ticks from SegmentIterator (non-blocking, uses internal buffer)
            const BATCH_SIZE = 10000; // Load 10K ticks at a time
            const tickBatch: TickData[] = [];

            // CRITICAL: Need to find ticks AFTER our current replay position
            const minEpoch = this.currentEpoch || 0;
            console.log('[ReplayController] Looking for ticks after:', new Date(minEpoch));

            // Strategy: Fast-forward through old ticks to find new data
            let skippedCount = 0;
            let foundNewData = false;

            // Phase 1: Fast-forward past old data
            while (!foundNewData && skippedCount < 100000) {
                // Safety limit: 100K ticks
                const tick = await this.segmentIterator.nextAsync();
                if (!tick) {
                    console.log('[ReplayController] SegmentIterator exhausted (no more data)');
                    break;
                }

                const tickEpoch = tick.epoch;

                if (tickEpoch > minEpoch) {
                    // Found a new tick! Add it to the batch and proceed to Phase 2
                    tickBatch.push(tick);
                    foundNewData = true;
                    break;
                }

                // Still old data, keep skipping
                skippedCount++;

                // Log progress every 10K skips
                if (skippedCount % 10000 === 0) {
                    console.log(
                        '[ReplayController] Still skipping old data... skipped',
                        skippedCount,
                        'ticks so far, last tick:',
                        new Date(tickEpoch)
                    );
                }
            }

            if (skippedCount > 0) {
                console.warn('[ReplayController] Fast-forwarded past', skippedCount, 'old ticks in SegmentIterator');
            }

            if (!foundNewData) {
                console.error(
                    '[ReplayController] Failed to find new ticks after skipping',
                    skippedCount,
                    'ticks - replay may have reached the end'
                );
                runInAction(() => {
                    this.isPlaying = false;
                    this.mode = 'paused';
                });
                return;
            }

            // Phase 2: Collect the rest of the batch (already have 1 tick)
            while (tickBatch.length < BATCH_SIZE) {
                const tick = await this.segmentIterator.nextAsync();
                if (!tick) break;
                tickBatch.push(tick);
            }

            console.log('[ReplayController] Collected', tickBatch.length, 'new ticks for EventLog refill');

            // Convert ticks to quotes
            const { segmentLoader } = await import('src/replay/SegmentLoader');
            const quotes = segmentLoader.ticksToQuotes(tickBatch);

            if (quotes.length === 0) {
                console.warn('[ReplayController] No quotes generated from tick batch');
                return;
            }

            // Add quotes to EventLog
            const { eventLog } = await import('src/replay/EventLog');
            const symbol = this.mainStore.chart.currentActiveSymbol?.symbol ?? '';
            eventLog.addHistorical(quotes, symbol);

            const elapsed = performance.now() - startTime;
            console.log(`[ReplayController] Added ${quotes.length} quotes to EventLog in ${elapsed.toFixed(0)}ms`);

            // Update progress tracking
            const iteratorState = this.segmentIterator.getState();
            runInAction(() => {
                this.streamingProgress.segmentsLoaded = iteratorState.segmentsLoaded;
            });

            // Recreate iterator from current position to pick up new events
            this.eventIterator = eventStream.iterator({
                startEpoch: this.currentEpoch!,
                endEpoch: this.replayEndEpoch!,
            });

            // Continue playback
            this._playNextBatch();
        } catch (error) {
            console.error('[ReplayController] Error refilling EventLog:', error);
            runInAction(() => {
                this.isPlaying = false;
                this.mode = 'paused';
            });
        }
    }

    private async _streamFromSegmentIterator() {
        if (!this.segmentIterator || !this.isPlaying) {
            console.log('[ReplayController] _streamFromSegmentIterator: missing iterator or not playing');
            return;
        }

        // Update progress phase
        runInAction(() => {
            this.streamingProgress.phase = 'loading_segments';
            this.streamingProgress.currentEpoch = this.currentEpoch;
        });

        const batchSize = this.scheduler.getBatchSize();
        const tickBatch: TickData[] = [];

        // Collect batch from SegmentIterator
        for (let i = 0; i < batchSize; i++) {
            const tick = await this.segmentIterator.nextAsync();
            if (!tick) break;
            tickBatch.push(tick);
        }

        if (tickBatch.length === 0) {
            // No more data from SegmentIterator
            console.log('[ReplayController] No more ticks from SegmentIterator, playback complete');
            runInAction(() => {
                this.isPlaying = false;
                this.mode = 'paused';
                this.streamingProgress.phase = 'idle';
            });
            return;
        }

        // Convert ticks to quotes and apply batch
        // NOTE: If there's a gap in data, we handle it gracefully by logging but continuing
        const firstTickEpoch = tickBatch[0].epoch;
        if (this.currentEpoch && firstTickEpoch > this.currentEpoch + 60000) {
            // Gap larger than 1 minute - log it
            const gapMinutes = Math.round((firstTickEpoch - this.currentEpoch) / 60000);
            console.warn(
                `[ReplayController] Data gap detected: ${gapMinutes} minutes between`,
                new Date(this.currentEpoch),
                'and',
                new Date(firstTickEpoch),
                '- continuing replay'
            );
        }

        // Convert ticks to quotes and apply batch
        const { segmentLoader } = await import('src/replay/SegmentLoader');
        const quotes = segmentLoader.ticksToQuotes(tickBatch);

        // Apply batch via chart adapter
        const shouldRender = !this.testMode || this.currentEventIndex % this.skipInterval === 0;
        if (shouldRender) {
            await this.mainStore.chartAdapter.applyReplayBatch(quotes, true);

            // On the first batch (replay just started), always scroll to the replay position
            // to ensure the chart shows the replay indicator. After that, scroll if shouldFollowReplay is true.
            const isFirstBatch = this.currentEventIndex === 0;
            console.log(
                '[ReplayController] Segment scroll check: isFirstBatch=',
                isFirstBatch,
                'shouldFollowReplay=',
                this.shouldFollowReplay,
                'currentEventIndex=',
                this.currentEventIndex
            );
            
            // Always enable following on first batch, then respect shouldFollowReplay preference
            if (isFirstBatch) {
                // Reset follow state on replay start
                runInAction(() => {
                    this.shouldFollowReplay = true;
                });
                console.log('[ReplayController] First batch (segment), scrolling to last tick after delay');
                setTimeout(() => {
                    this.mainStore.chartAdapter.markProgrammaticScroll();
                    this.mainStore.chartAdapter.flutterChart?.app.scrollToLastTick();
                    console.log('[ReplayController] scrollToLastTick called for first batch (segment)');
                }, 200);
            } else if (this.shouldFollowReplay) {
                // Keep following the replay cursor
                console.log('[ReplayController] Following replay cursor (segment), calling scrollToLastTick');
                this.mainStore.chartAdapter.markProgrammaticScroll();
                this.mainStore.chartAdapter.flutterChart?.app.scrollToLastTick();
            } else {
                console.log('[ReplayController] Not following (segment), skipping scrollToLastTick');
            }
        }

        // Update state
        const lastTick = tickBatch[tickBatch.length - 1];

        // Stop streaming if we've reached the replay end time
        if (this.replayEndEpoch && lastTick.epoch >= this.replayEndEpoch) {
            console.log(
                '[ReplayController] Reached replay end epoch',
                new Date(this.replayEndEpoch),
                'stopping playback'
            );
            runInAction(() => {
                this.currentEpoch = Math.min(lastTick.epoch, this.replayEndEpoch!);
                this.currentEventIndex += tickBatch.length;
                this.isPlaying = false;
                this.mode = 'paused';
                this.streamingProgress.phase = 'idle';
            });
            return;
        }

        runInAction(() => {
            this.currentEpoch = lastTick.epoch;
            this.currentEventIndex += tickBatch.length;

            // Update segment streaming progress
            if (this.segmentIterator) {
                const iteratorState = this.segmentIterator.getState();
                this.streamingProgress.segmentsLoaded = iteratorState.segmentsLoaded;
                this.streamingProgress.totalSegments = iteratorState.totalSegments;
            }
        });

        // Optionally add to EventLog window for seek support (if windowing enabled)
        try {
            const { eventLog } = await import('src/replay/EventLog');
            if (eventLog && this.mainStore.chart.currentActiveSymbol) {
                eventLog.addHistorical(quotes, this.mainStore.chart.currentActiveSymbol.symbol);
            }
        } catch (err) {
            // EventLog may not exist in all contexts
            console.debug('[ReplayController] Could not add to EventLog during segment streaming');
        }

        const progress = this.progress.toFixed(2);
        const bufferInfo = this.segmentIterator.getState();
        console.log(
            '[ReplayController] Segment batch applied:',
            tickBatch.length,
            'ticks from',
            new Date(tickBatch[0].epoch),
            'to',
            new Date(lastTick.epoch),
            'progress:',
            `${progress}%`,
            'buffer:',
            bufferInfo.bufferSize,
            'segments:',
            `${bufferInfo.segmentsLoaded}/${bufferInfo.totalSegments}`
        );

        // Check if we need to load more segments
        if (bufferInfo.bufferSize < 5000 && bufferInfo.segmentsLoaded < bufferInfo.totalSegments) {
            console.log('[ReplayController] Buffer low, triggering segment load...');
        }

        // Schedule next batch
        const delay = this.scheduler.getFrameDelayMs();
        if (delay > 0) {
            this.playbackTimer = setTimeout(() => this._streamFromSegmentIterator(), delay);
        } else {
            this.animationFrameId = requestAnimationFrame(() => this._streamFromSegmentIterator());
        }
    }

    private async _applyEvent(event: TTickEvent) {
        // Convert event back to TQuote format
        const quote = event.raw;

        console.log('[ReplayController] Applying replay event:', quote.Date, 'epoch:', quote.DT?.getTime());
        // Use replay API for incremental application during playback
        await this.mainStore.chartAdapter.applyReplayBatch([quote], true);
    }

    /**
     * Estimate the number of ticks in a time range based on symbol and granularity.
     * Used to determine whether to use direct API load (<5000 ticks) or chunked mode (≥5000 ticks).
     *
     * @param symbol - Trading symbol (e.g., 'R_25', 'frxEURUSD')
     * @param startEpoch - Start time in milliseconds
     * @param endEpoch - End time in milliseconds
     * @param granularity - Candle granularity in seconds (0 for ticks)
     * @returns Estimated tick count
     */
    private _estimateTickCount(symbol: string, startEpoch: number, endEpoch: number, granularity: number): number {
        const rangeMs = endEpoch - startEpoch;
        const rangeSeconds = rangeMs / 1000;

        // For candle granularities, calculation is straightforward
        if (granularity > 0) {
            const estimatedCandles = Math.ceil(rangeSeconds / granularity);
            console.log(
                '[ReplayController] Estimated candles:',
                estimatedCandles,
                'for granularity',
                granularity,
                'seconds'
            );
            return estimatedCandles;
        }

        // For tick data (granularity = 0), use heuristics based on symbol type
        // These are conservative estimates based on typical market activity

        let ticksPerSecond = 0.5; // Default: 1 tick per 2 seconds

        // Volatility indices (R_10, R_25, R_50, R_75, R_100, etc.)
        if (symbol.startsWith('R_')) {
            const tickRate = parseInt(symbol.substring(2), 10);
            if (!isNaN(tickRate)) {
                // R_10 = 10 ticks/sec, R_25 = 25 ticks/sec, etc.
                // But actual rate is lower due to generation algorithm
                ticksPerSecond = tickRate / 100; // Conservative estimate
            } else {
                ticksPerSecond = 0.5; // Fallback for unrecognized R_ symbols
            }
        }
        // Forex pairs (typically lower tick frequency)
        else if (symbol.startsWith('frx')) {
            ticksPerSecond = 0.2; // ~1 tick per 5 seconds during active hours
        }
        // Crypto pairs (higher volatility)
        else if (symbol.startsWith('cry') || symbol.includes('BTC') || symbol.includes('ETH')) {
            ticksPerSecond = 1.0; // ~1 tick per second
        }
        // Stock indices
        else if (symbol.includes('OTC') || symbol.match(/^[A-Z]{3,4}$/)) {
            ticksPerSecond = 0.3; // ~1 tick per 3 seconds
        }

        const estimatedTicks = Math.ceil(rangeSeconds * ticksPerSecond);

        console.log(
            '[ReplayController] Estimated ticks:',
            estimatedTicks,
            'for symbol',
            symbol,
            '(',
            ticksPerSecond.toFixed(2),
            'ticks/sec over',
            Math.round(rangeSeconds / 60),
            'minutes)'
        );

        return estimatedTicks;
    }

    // Default granularity for replay mode (1 minute = 60 seconds)
    private static readonly REPLAY_DEFAULT_GRANULARITY = 60;

    private async _fetchHistoricalDataForReplay(symbol: string, targetEpoch: number) {
        // Load data from targetEpoch (user's selected start time) forward to current time
        // IMPORTANT: Replay mode only supports candles (1 minute minimum) to avoid
        // epoch collision issues with tick data. Force granularity to at least 60 seconds.
        const stateGranularity = this.mainStore.state.granularity || 0;
        const granularity = stateGranularity < ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY 
            ? ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY 
            : stateGranularity;
        
        const startEpoch = targetEpoch;
        const endEpoch = Date.now();
        const requestedRangeMs = endEpoch - startEpoch;

        const oneDayMs = 24 * 60 * 60 * 1000;
        const windowingThresholdMs = 7 * oneDayMs; // Enable windowing only for week+ replays

        console.log('═══════════════════════════════════════════════════════════');
        console.log('[ReplayController] 🔍 HISTORICAL DATA REQUEST (CANDLE MODE)');
        console.log('═══════════════════════════════════════════════════════════');
        console.log('[ReplayController] Symbol:', symbol);
        console.log('[ReplayController] Requested granularity:', stateGranularity, 'seconds');
        console.log('[ReplayController] Effective granularity:', granularity, 'seconds (min: 60s for replay)');
        console.log('[ReplayController] Selected Date/Time:', new Date(startEpoch).toLocaleString());
        console.log(
            '[ReplayController] Date Range:',
            new Date(startEpoch).toLocaleString(),
            '→',
            new Date(endEpoch).toLocaleString()
        );
        console.log('[ReplayController] Range Duration:', Math.round(requestedRangeMs / (24 * 60 * 60 * 1000)), 'days');
        console.log('[ReplayController] Start Epoch (ms):', startEpoch);
        console.log('[ReplayController] End Epoch (ms):', endEpoch);
        console.log('═══════════════════════════════════════════════════════════');

        // Import once at the beginning
        const { eventLog } = await import('src/replay/EventLog');
        const { segmentLoader } = await import('src/replay/SegmentLoader');

        // Enable EventLog windowing only for long replays to prevent memory bloat
        if (requestedRangeMs >= windowingThresholdMs) {
            const windowSize = 100_000; // Keep 100K events in memory for very long ranges
            eventLog.enableWindowing(windowSize);
            console.log('[ReplayController] Enabled EventLog windowing for long replay', {
                windowSize,
                rangeHours: Math.round(requestedRangeMs / 3600000),
            });
        } else {
            eventLog.disableWindowing();
            console.log('[ReplayController] Windowing disabled for short-range replay; keeping full buffer');
        }

        // Step 1: Estimate tick count to determine loading strategy
        const estimatedTicks = this._estimateTickCount(symbol, startEpoch, endEpoch, granularity);
        const API_TICK_LIMIT = 5000;

        // CRITICAL: Only use segment streaming for 180+ day replays (6+ months)
        // For shorter periods (< 180 days), always use Deriv API to get real tick data
        // Segments may contain aggregated hourly/daily data which creates straight lines
        const oneEightyDaysMs = 180 * 24 * 60 * 60 * 1000;
        const useChunkedMode = estimatedTicks >= API_TICK_LIMIT && requestedRangeMs >= oneEightyDaysMs;

        if (useChunkedMode) {
            console.log(
                '[ReplayController] Estimated',
                estimatedTicks,
                'ticks (≥',
                API_TICK_LIMIT,
                ') and range ≥ 180 days - using SEGMENT STREAMING mode'
            );

            // For large replays, use ONLY SegmentIterator (no API chunked fetch)
            // This avoids the complexity of mixing API chunks with segment data
            // SegmentIterator will handle all data loading lazily during playback

            // Clear EventLog and initialize SegmentIterator
            eventLog.clear();

            console.log(
                '[ReplayController] Initializing SegmentIterator for streaming replay from',
                new Date(startEpoch),
                'to',
                new Date(endEpoch)
            );

            const { createSegmentIterator } = await import('src/replay/SegmentIterator');
            console.log('[ReplayController] Creating SegmentIterator with params:', {
                symbol,
                startEpoch,
                endEpoch,
                startDate: new Date(startEpoch),
                endDate: new Date(endEpoch),
                granularity,
                bufferSize: 50000,
            });
            this.segmentIterator = await createSegmentIterator({
                symbol,
                startEpoch,
                endEpoch,
                granularity,
                bufferSize: 50000,
                lazyLoad: true,
            });

            console.log('[ReplayController] SegmentIterator initialized, preloading initial batch...');

            // Load initial batch of 30K ticks to populate EventLog for startup
            const INITIAL_BATCH_SIZE = 30000;
            const initialTicks: any[] = [];

            for (let i = 0; i < INITIAL_BATCH_SIZE; i++) {
                const tick = await this.segmentIterator.nextAsync();
                if (!tick) break;
                initialTicks.push(tick);
            }

            console.log('[ReplayController] Preloaded', initialTicks.length, 'ticks from SegmentIterator');

            // Check tick density to detect if we have tick data or candle data
            if (initialTicks.length >= 2) {
                const avgGapMs =
                    (initialTicks[initialTicks.length - 1].epoch - initialTicks[0].epoch) / initialTicks.length;
                const avgGapMinutes = Math.round(avgGapMs / 60000);
                console.log('[ReplayController] 📊 TICK DENSITY ANALYSIS:');
                console.log('[ReplayController] Total ticks:', initialTicks.length);
                console.log(
                    '[ReplayController] Time span:',
                    new Date(initialTicks[0].epoch).toLocaleString(),
                    '→',
                    new Date(initialTicks[initialTicks.length - 1].epoch).toLocaleString()
                );
                console.log('[ReplayController] Average gap between ticks:', avgGapMinutes, 'minutes');
                console.log(
                    '[ReplayController] First 5 ticks:',
                    initialTicks
                        .slice(0, 5)
                        .map((t: any) => ({ time: new Date(t.epoch).toLocaleString(), price: t.price }))
                );

                if (avgGapMinutes > 30) {
                    console.warn(
                        '[ReplayController] ⚠️  WARNING: Data appears to be aggregated (',
                        avgGapMinutes,
                        'min avg gap)'
                    );
                    console.warn(
                        '[ReplayController] Expected: Continuous tick stream with seconds/milliseconds between ticks'
                    );
                    console.warn('[ReplayController] Actual: Hourly or daily candle data');
                }
            }

            // If no segments were found, fall back to API fetch
            if (initialTicks.length === 0) {
                console.warn('[ReplayController] No segments found for date range. Falling back to API fetch...');
                this.segmentIterator = undefined; // Clean up
                // Continue to API fetch code below (don't return early)
            } else {
                // Segments found - use segment streaming
                const quotes = segmentLoader.ticksToQuotes(initialTicks);
                eventLog.addHistorical(quotes, symbol);
                console.log('[ReplayController] Added', quotes.length, 'quotes to EventLog');

                // IMPORTANT: Show only the first tick on the chart initially
                // The rest will be added progressively during playback
                // This prevents the chart from jumping to 30K ticks ahead
                if (quotes.length > 0) {
                    await this.mainStore.chartAdapter.applyReplayBatch([quotes[0]], false); // false = replace, not append
                    console.log('[ReplayController] Initialized chart with first tick:', quotes[0].Date);
                }

                runInAction(() => {
                    this.streamingProgress.totalSegments = this.segmentIterator?.getRemainingCount() ?? 0;
                    this.streamingProgress.segmentsLoaded = 0;
                });

                return; // Early return after segment-based initialization
            }
        } else {
            console.log(
                '[ReplayController] Estimated',
                estimatedTicks,
                'ticks (<',
                API_TICK_LIMIT,
                ') - using DIRECT load'
            );
        }

        // NEW: Track loading progress
        runInAction(() => {
            this.streamingProgress.phase = 'loading_initial';
            this.streamingProgress.initialWindowLoaded = false;
            this.streamingProgress.targetEpoch = targetEpoch;
        }); // Load only initial window first (e.g., 6 hours) for fast startup
        // Then stream remaining data during playback
        // Use full range to current time to ensure we get all available data
        const initialEndEpoch = endEpoch; // Changed: use full range to current time instead of 6-hour window

        console.log(
            '[ReplayController] Streaming mode: loading initial window from',
            new Date(startEpoch),
            'to',
            new Date(initialEndEpoch)
        );

        // Shared helper to fetch paginated data from API
        const feed = this.mainStore.chart.feed;

        // Helper for filling small gaps with precise range control (not for pagination)
        const fetchHistoryRange = async (rangeStart: number, rangeEnd: number): Promise<TQuote[]> => {
            if (!feed) {
                return [];
            }

            const startEpochSec = Math.floor(rangeStart / 1000);
            const endEpochSec = Math.floor(rangeEnd / 1000);

            console.log(
                '[ReplayController] Gap fill request:',
                new Date(rangeStart).toLocaleString(),
                '→',
                new Date(rangeEnd).toLocaleString()
            );

            try {
                const response = await feed._binaryApi.getTickHistory({
                    symbol,
                    granularity: granularity as any,
                    start: startEpochSec,
                    end: String(endEpochSec),
                });

                if (response?.error) {
                    console.error('[ReplayController] Gap fill error:', response.error);
                    return [];
                }

                const { TickHistoryFormatter } = await import('src/feed/TickHistoryFormatter');
                const quotes = TickHistoryFormatter.formatHistory(response) ?? [];
                console.log('[ReplayController] Gap filled with', quotes.length, 'quotes');
                
                // Log sample timestamps to diagnose tick vs minute data
                if (quotes.length >= 3) {
                    const first = quotes[0];
                    const second = quotes[1];
                    const third = quotes[2];
                    const intervalMs = (second.DT?.getTime() ?? 0) - (first.DT?.getTime() ?? 0);
                    console.log('[ReplayController] Sample timestamps from API response:');
                    console.log('  [0]:', first.DT?.toISOString());
                    console.log('  [1]:', second.DT?.toISOString());
                    console.log('  [2]:', third.DT?.toISOString());
                    console.log('  Interval between [0] and [1]:', intervalMs, 'ms',
                        intervalMs === 1000 ? '(TICK DATA ✓)' : 
                        intervalMs >= 60000 ? '(MINUTE CANDLE DATA ⚠️)' : '(irregular)');
                }
                
                return quotes;
            } catch (error) {
                console.error('[ReplayController] Gap fill exception:', error);
                return [];
            }
        };

        // Clear the playback buffer first to avoid conflicts with old data
        segmentLoader.clearPlaybackBuffer(symbol, granularity);
        console.log('[ReplayController] Cleared playback buffer for fresh load');

        // Try loading INITIAL WINDOW from segments first (skipTrimming=true to preserve all historical data for replay)
        console.log('[ReplayController] ───────────────────────────────────────');
        console.log('[ReplayController] 💾 CHECKING INDEXEDDB SEGMENTS');
        console.log(
            '[ReplayController] Looking for segments in range:',
            new Date(startEpoch).toLocaleString(),
            '→',
            new Date(initialEndEpoch).toLocaleString()
        );

        const ticksLoaded = await segmentLoader.loadSegmentsForRange(
            symbol,
            startEpoch,
            initialEndEpoch,
            granularity,
            true
        );

        if (ticksLoaded > 0) {
            console.log('[ReplayController] ✅ FOUND SEGMENTS:', ticksLoaded, 'ticks loaded from IndexedDB');
            console.log('[ReplayController] ───────────────────────────────────────');

            // Get ticks from buffer and convert to quotes
            const ticks = segmentLoader.getTicksFromBuffer(symbol, startEpoch, initialEndEpoch, granularity);
            console.log('[ReplayController] getTicksFromBuffer returned', ticks.length, 'ticks for initial window');

            let quotes = segmentLoader.ticksToQuotes(ticks);
            console.log('[ReplayController] ticksToQuotes returned', quotes.length, 'quotes for initial window');
            
            // Log sample quote epochs after conversion (should now be unique)
            if (quotes.length >= 5) {
                console.log('[ReplayController] 📊 SAMPLE QUOTE EPOCHS (after uniquification):');
                for (let i = 0; i < 5; i++) {
                    console.log(`  [${i}]: ${quotes[i].DT?.toISOString()} (epoch: ${quotes[i].DT?.getTime()})`);
                }
                if (quotes[1].DT && quotes[0].DT) {
                    const interval = quotes[1].DT.getTime() - quotes[0].DT.getTime();
                    console.log(`  Interval between [0] and [1]: ${interval}ms`,
                        interval === 1 ? '(unique sub-ms offsets ✓)' :
                        interval === 0 ? '(STILL DUPLICATES ⚠️)' : '(natural spacing ✓)');
                }
            }

            // Backfill gaps if segments do not cover requested initial window
            const firstQuoteEpoch = quotes[0]?.DT?.getTime() ?? null;
            const lastQuoteEpoch = quotes[quotes.length - 1]?.DT?.getTime() ?? null;
            const needsHeadFill = firstQuoteEpoch !== null && firstQuoteEpoch > startEpoch + 1000;
            const needsTailFill = lastQuoteEpoch !== null && lastQuoteEpoch < initialEndEpoch - 1000;

            console.log(
                '[ReplayController] Segment coverage check: firstQuoteEpoch=',
                new Date(firstQuoteEpoch ?? 0),
                'lastQuoteEpoch=',
                new Date(lastQuoteEpoch ?? 0),
                'requestedStart=',
                new Date(startEpoch),
                'requestedEnd=',
                new Date(initialEndEpoch),
                'needsHeadFill=',
                needsHeadFill,
                'needsTailFill=',
                needsTailFill
            );

            if ((needsHeadFill || needsTailFill) && feed) {
                console.warn(
                    '[ReplayController] Segment coverage gap detected in initial window; fetching missing ranges',
                    {
                        needsHeadFill,
                        needsTailFill,
                        firstQuoteEpoch,
                        lastQuoteEpoch,
                        requestedStart: startEpoch,
                        requestedEnd: initialEndEpoch,
                    }
                );
                const headQuotes = needsHeadFill ? await fetchHistoryRange(startEpoch, firstQuoteEpoch - 1) : [];
                const tailQuotes = needsTailFill
                    ? await fetchHistoryRange((lastQuoteEpoch ?? initialEndEpoch) + 1, initialEndEpoch)
                    : [];
                quotes = [...headQuotes, ...quotes, ...tailQuotes].sort(
                    (a, b) => (a.DT?.getTime() ?? 0) - (b.DT?.getTime() ?? 0)
                );
                const combinedFirst = quotes[0]?.DT;
                const combinedLast = quotes[quotes.length - 1]?.DT;
                console.log(
                    '[ReplayController] After filling gaps, initial window quote time range:',
                    combinedFirst,
                    'to',
                    combinedLast,
                    'total:',
                    quotes.length
                );
            }

            // Clear EventLog and add fresh data (windowing is enabled)
            eventLog.clear();

            if (quotes.length > 0) {
                console.log(
                    '[ReplayController] About to add',
                    quotes.length,
                    'quotes to EventLog (windowing enabled)...'
                );
                console.log('[ReplayController] Quote time range:', quotes[0].DT, 'to', quotes[quotes.length - 1].DT);
                const startTime = performance.now();

                // Batch mode: add all quotes without sorting until done
                eventLog.beginBatchAdd();

                // Add in chunks to avoid blocking main thread
                const CHUNK_SIZE = 10000;
                let lastChunkTime = performance.now();
                for (let i = 0; i < quotes.length; i += CHUNK_SIZE) {
                    const chunk = quotes.slice(i, i + CHUNK_SIZE);
                    eventLog.addHistorical(chunk, symbol);
                    const chunkNumber = Math.floor(i / CHUNK_SIZE) + 1;
                    const totalChunks = Math.ceil(quotes.length / CHUNK_SIZE);
                    const elapsed = performance.now() - lastChunkTime;
                    console.log(
                        `[ReplayController] Added chunk ${chunkNumber}/${totalChunks} in ${elapsed.toFixed(0)}ms`
                    );
                    lastChunkTime = performance.now();

                    // Yield to browser every chunk
                    if (i + CHUNK_SIZE < quotes.length) {
                        console.log(`[ReplayController] Yielding to browser before chunk ${chunkNumber + 1}...`);
                        await new Promise(resolve => setTimeout(resolve, 0));
                        const yieldTime = performance.now() - lastChunkTime;
                        console.log(`[ReplayController] Browser yield took ${yieldTime.toFixed(0)}ms`);
                        lastChunkTime = performance.now();
                    }
                }

                // End batch mode and do single sort at end
                console.log('[ReplayController] Finalizing batch add for initial window...');
                const finalizeStart = performance.now();
                await eventLog.endBatchAdd();
                const finalizeElapsed = performance.now() - finalizeStart;
                console.log('[ReplayController] Batch finalization took', finalizeElapsed.toFixed(0), 'ms');

                const windowBounds = eventLog.getWindowBounds();
                console.log('[ReplayController] EventLog window bounds:', {
                    start: new Date(windowBounds.start),
                    end: new Date(windowBounds.end),
                    size: windowBounds.size,
                    mode: windowBounds.mode,
                });

                const elapsed = performance.now() - startTime;
                console.log(
                    '[ReplayController] Added',
                    quotes.length,
                    'quotes from segments to EventLog in',
                    elapsed.toFixed(0),
                    'ms'
                );
                const finalBounds = eventStream.getBounds();
                console.log('[ReplayController] EventLog now has bounds:', finalBounds);
                console.log(
                    '[ReplayController] EventLog bounds: first=',
                    new Date(finalBounds.first ?? 0),
                    'last=',
                    new Date(finalBounds.last ?? 0)
                );
                console.log('[ReplayController] EventLog total size:', eventLog.size(), 'events');

                // NEW: Mark initial window loaded
                runInAction(() => {
                    this.streamingProgress.initialWindowLoaded = true;
                });

                // NEW: Initialize SegmentIterator for lazy loading future data
                try {
                    const { createSegmentIterator } = await import('src/replay/SegmentIterator');
                    this.segmentIterator = await createSegmentIterator({
                        symbol,
                        startEpoch: initialEndEpoch, // Start from end of initial window
                        endEpoch,
                        granularity,
                        bufferSize: 50000, // Buffer 50K ticks ahead
                        lazyLoad: true, // Enable lazy loading
                    });

                    console.log(
                        '[ReplayController] SegmentIterator initialized for streaming from',
                        new Date(initialEndEpoch),
                        'to',
                        new Date(endEpoch)
                    );

                    // NEW: Track total segments for progress
                    runInAction(() => {
                        this.streamingProgress.totalSegments = this.segmentIterator?.getRemainingCount() ?? 0;
                        this.streamingProgress.segmentsLoaded = 0;
                        this.streamingProgress.phase = 'idle';
                    });
                } catch (err) {
                    console.warn('[ReplayController] Failed to initialize SegmentIterator:', err);
                    runInAction(() => {
                        this.streamingProgress.phase = 'idle';
                    });
                }

                return; // Successfully loaded from segments (plus gap fill if needed)
            } else {
                console.warn('[ReplayController] No quotes extracted from buffer; falling through to API fetch...');
            }
        }

        // Fallback: fetch from API if no segments available or segment load failed
        // Lazy pagination: only fetch the FIRST page (up to 5000 candles) for fast startup.
        // Subsequent pages will be fetched on-demand during playback when the EventLog is exhausted.
        console.log('[ReplayController] No segments found or empty, fetching first page from API...');

        if (!feed) {
            console.warn('[ReplayController] Cannot fetch first page: no feed available');
            return;
        }

        const firstPageStartSec = Math.floor(startEpoch / 1000);
        const firstPageEndMs = Math.min(startEpoch + 5000 * granularity * 1000, endEpoch);
        const firstPageEndSec = Math.floor(firstPageEndMs / 1000);

        const firstPageResponse = await feed._binaryApi.getTickHistory({
            symbol,
            granularity: granularity as any,
            start: firstPageStartSec,
            end: String(firstPageEndSec),
            count: 5000,
        });

        if (firstPageResponse?.error) {
            console.error('[ReplayController] Error fetching first page:', firstPageResponse.error);
            return;
        }

        const { TickHistoryFormatter } = await import('src/feed/TickHistoryFormatter');
        const apiQuotes = TickHistoryFormatter.formatHistory(firstPageResponse) ?? [];

        if (!apiQuotes.length) {
            console.warn('[ReplayController] No quotes returned from first API page');
            return;
        }

        console.log('[ReplayController] First page received:', apiQuotes.length, 'candles');

        // Track the end epoch of loaded data for lazy pagination during playback
        const lastQuoteEpoch = apiQuotes[apiQuotes.length - 1]?.DT?.getTime();
        if (lastQuoteEpoch) {
            runInAction(() => {
                this.lastFetchedPageEndEpoch = lastQuoteEpoch;
                this.totalEvents = apiQuotes.length;
                console.log('[ReplayController] Data loaded up to:', new Date(lastQuoteEpoch).toLocaleString());
            });
        }

        eventLog.clear();

        console.log('[ReplayController] About to add', apiQuotes.length, 'API quotes to EventLog...');
        eventLog.beginBatchAdd();
        eventLog.addHistorical(apiQuotes, symbol);
        await eventLog.endBatchAdd();
        console.log('[ReplayController] EventLog now has bounds:', eventStream.getBounds());

        // Mark initial window loaded
        runInAction(() => {
            this.streamingProgress.initialWindowLoaded = true;
            this.streamingProgress.phase = 'idle';
        });
    }

    private async _resetChartToEpoch(targetEpoch: number) {
        console.log('[ReplayController] _resetChartToEpoch starting for epoch:', new Date(targetEpoch));
        const resetStartTime = performance.now();

        // Reset hasReachedEndOfData to allow left-scroll pagination during replay
        // This enables loading tick history prior to the replay start date
        if (this.mainStore.chart.feed) {
            this.mainStore.chart.feed.setHasReachedEndOfData(false);
            console.log('[ReplayController] Reset hasReachedEndOfData to allow pagination');
        }

        const bounds = eventStream.getBounds();
        if (!bounds.first) {
            console.warn('[ReplayController] _resetChartToEpoch: no bounds available, resetting to empty state');
            // Reset chart to targetEpoch with no data
            await this.mainStore.chartAdapter.resetToEpoch(targetEpoch);
            return;
        }

        // For backtesting mode: Start with completely empty chart at target time
        // This prevents showing historical data before the selected start time
        // The chart will build up as replay progresses forward from the start point
        console.log('[ReplayController] Starting with empty chart at target epoch:', new Date(targetEpoch));
        await this.mainStore.chartAdapter.resetToEpoch(targetEpoch);

        // Set cursor position without scrolling (chart is empty, will auto-follow as data comes in)
        // Don't call ensureCursorVisible here as it might cause unwanted scrolling

        const resetElapsed = performance.now() - resetStartTime;
        console.log('[ReplayController] _resetChartToEpoch completed in', resetElapsed.toFixed(0), 'ms');

        runInAction(() => {
            if (!this.currentEventIndex) {
                this.currentEventIndex = 0;
                this.totalEvents = 0;
            }
        });
    }

    /**
     * Fill a gap in historical data by fetching from API in paginated chunks.
     * This is used for replay mode to load all data from a selected date to current time.
     * Only supports candle data (minimum 60 second granularity).
     */
    /**
     * Fetch the next page of historical data when approaching the end of current page.
     * Returns true if new data was fetched, false otherwise.
     */
    private async _fetchNextPage(): Promise<boolean> {
        if (!this.lastFetchedPageEndEpoch) {
            console.warn('[ReplayController] Cannot fetch next page: no lastFetchedPageEndEpoch');
            return false;
        }

        const symbol = this.mainStore.chart.currentActiveSymbol?.symbol;
        if (!symbol) {
            console.warn('[ReplayController] Cannot fetch next page: no active symbol');
            return false;
        }

        // Force candle mode for replay (minimum 60 seconds granularity)
        const stateGranularity = this.mainStore.state.granularity || 0;
        const granularity = stateGranularity < ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY 
            ? ReplayControllerStore.REPLAY_DEFAULT_GRANULARITY 
            : stateGranularity;
            
        const feed = this.mainStore.chart.feed;
        if (!feed) {
            console.warn('[ReplayController] Cannot fetch next page: no feed');
            return false;
        }

        // Start from the end of the last page (add granularity interval to avoid overlap)
        const nextStartEpoch = this.lastFetchedPageEndEpoch + (granularity * 1000);

        // Cap the end epoch at the replay end boundary (Date.now() at play time)
        const durationMs = 5000 * granularity * 1000;
        const nextEndEpoch = Math.min(nextStartEpoch + durationMs, this.replayEndEpoch ?? Infinity);

        console.log('[ReplayController] 📄 FETCHING NEXT PAGE');
        console.log(
            '[ReplayController] From:',
            new Date(nextStartEpoch).toLocaleString(),
            'To:',
            new Date(nextEndEpoch).toLocaleString()
        );

        try {
            const startEpochSec = Math.floor(nextStartEpoch / 1000);
            const endEpochSec = Math.floor(nextEndEpoch / 1000);

            const response = await feed._binaryApi.getTickHistory({
                symbol,
                granularity: granularity as any,
                start: startEpochSec,
                end: String(endEpochSec),
                count: 5000,
            });

            if (response?.error) {
                console.error('[ReplayController] Error fetching next page:', response.error);
                return false;
            }

            const { TickHistoryFormatter } = await import('src/feed/TickHistoryFormatter');
            const quotes = TickHistoryFormatter.formatHistory(response) ?? [];

            console.log('[ReplayController] ✅ Next page received:', quotes.length, 'quotes');

            if (quotes.length === 0) {
                console.warn('[ReplayController] No more data available, reached end of historical data');
                return false;
            }

            // Add new quotes to EventLog
            const { eventLog } = await import('src/replay/EventLog');
            eventLog.addHistorical(quotes, symbol);

            // Update last fetched page end epoch
            const lastQuoteEpoch = quotes[quotes.length - 1]?.DT?.getTime();
            if (lastQuoteEpoch) {
                runInAction(() => {
                    this.lastFetchedPageEndEpoch = lastQuoteEpoch;
                    this.totalEvents += quotes.length;
                    console.log(
                        '[ReplayController] Updated lastFetchedPageEndEpoch to:',
                        new Date(lastQuoteEpoch).toLocaleString()
                    );
                    console.log('[ReplayController] Total events now:', this.totalEvents);
                });
            }

            return true;
        } catch (error) {
            console.error('[ReplayController] Exception fetching next page:', error);
            return false;
        }
    }

    /**
     * Fetch the next API page and continue playback.
     * Called from the EventLog exhaustion handler when no SegmentIterator is available
     * but there is still more historical data to fetch from the API.
     */
    private async _fetchNextPageAndContinue() {
        if (!this.isPlaying) {
            console.log('[ReplayController] _fetchNextPageAndContinue: not playing, aborting');
            return;
        }

        try {
            console.log('[ReplayController] ===== Fetching next API page to continue playback =====');

            const gotData = await this._fetchNextPage();

            if (!gotData) {
                console.log('[ReplayController] No more data from API, pausing playback');
                runInAction(() => {
                    this.isPlaying = false;
                    this.mode = 'paused';
                });
                return;
            }

            // Recreate iterator from current position to pick up new events
            this.eventIterator = eventStream.iterator({
                startEpoch: this.currentEpoch!,
                endEpoch: this.replayEndEpoch!,
            });

            console.log('[ReplayController] EventIterator recreated, continuing playback');

            // Continue playback
            this._playNextBatch();
        } catch (error) {
            console.error('[ReplayController] Error in _fetchNextPageAndContinue:', error);
            runInAction(() => {
                this.isPlaying = false;
                this.mode = 'paused';
            });
        }
    }
}
